// src/ChainRegistry.ts
import { CustomError as CustomError22 } from "@umami/utils";

// src/providers/BaseProviders/BaseEvmProvider.ts
import { CustomError as CustomError18 } from "@umami/utils";

// src/providers/BaseProviders/BaseProvider.ts
import { CustomError as CustomError17 } from "@umami/utils";

// src/utils/chains.ts
import { CustomError as CustomError4 } from "@umami/utils";

// src/providers/consts.ts
var ChainIds = {
  TEZOS: "tezos",
  ETHERLINK: "etherlink",
  ETHEREUM: "ethereum",
  BITCOIN: "bitcoin",
  BSC: "bsc"
};
var ChainNamespaces = {
  EVM: "eip155",
  TEZOS: "tezos",
  BITCOIN: "bitcoin"
};
var TransactionDirections = {
  SEND: "send",
  RECEIVE: "receive"
};
var OperationTypes = {
  TRANSACTION: "transaction",
  TOKEN_TRANSFER: "token_transfer",
  CONTRACT_CALL: "contract_call",
  TOKEN_MINTING: "token_minting",
  SAFE_SEND: "safe_send",
  TOKEN_BURNING: "token_burning",
  SHIELD: "shield",
  UNSHIELD: "unshield",
  SAPLING: "sapling"
};
var CryptoSymbols = {
  [ChainIds.TEZOS]: "XTZ",
  [ChainIds.ETHERLINK]: "XTZ",
  [ChainIds.ETHEREUM]: "ETH",
  [ChainIds.BSC]: "BNB",
  [ChainIds.BITCOIN]: "BTC"
};
var DEPRECATED_CHAINS = [
  "eip155:128123",
  "eip155:5",
  "tezos:ghostnet",
  "tezos:tezlink-shadownet"
];

// src/utils/configs.ts
var getChainFullId = (namespace, identifier) => `${namespace}:${identifier}`;
var getChainFullIdFromNetwork = (network) => getChainFullId(network.namespace, network.identifier);
var toTransformedKey = (name) => {
  let normalized;
  if (name === "mainnet") {
    normalized = "Tezos";
  } else if (name === "shadownet") {
    normalized = "Tezos Shadownet";
  } else if (name === "michelson") {
    normalized = "Tezos Michelson";
  } else if (name === "michelson shadownet") {
    normalized = "Tezos Michelson Shadownet";
  } else if (name === "previewnet") {
    normalized = "Tezos Previewnet";
  } else {
    normalized = name;
  }
  return normalized.replace(/\s+/g, "_").toUpperCase();
};
var generateChainFullIdsFromConfigs = (configs) => {
  const entries = configs.map((config) => {
    const key = toTransformedKey(config.name);
    const value = getChainFullIdFromNetwork(config);
    return [key, value];
  });
  return Object.fromEntries(entries);
};

// src/env.ts
import { getEnv } from "@umami/utils";
var PRICING_API_URL = getEnv("VITE_PRICING_API_URL");
var ETHEREUM_NETWORK_CONFIG = getEnv("VITE_ETHEREUM_NETWORK_CONFIG");
var ETHERLINK_NETWORK_CONFIG = getEnv("VITE_ETHERLINK_NETWORK_CONFIG");
var IS_STAGING = getEnv("VITE_IS_STAGING") === "true";

// src/providers/bitcoin/utils/selection.ts
import { CustomError as CustomError2 } from "@umami/utils";
import coinselect from "coinselect";

// src/providers/bitcoin/utils/utils.ts
import { CustomError } from "@umami/utils";
import * as bitcoin from "bitcoinjs-lib";
var SATS_PER_BTC = 100000000n;
var BTC_DECIMALS = 8;
var BITCOIN_ADDRESS_TYPES = {
  P2TR: "p2tr",
  P2WPKH: "p2wpkh",
  P2SH_P2WPKH: "p2sh_p2wpkh",
  P2PKH: "p2pkh"
};
var PATH_PURPOSE = {
  P2PKH: "44",
  P2SH_P2WPKH: "49",
  P2WPKH: "84",
  P2TR: "86"
};
var isBitcoinAddressValid = (address3) => [bitcoin.networks.bitcoin, bitcoin.networks.testnet].some((network) => {
  try {
    bitcoin.address.toOutputScript(address3, network);
    return true;
  } catch {
    return false;
  }
});
var parseBitcoinAddress = (address3) => {
  if (!isBitcoinAddressValid(address3)) {
    throw new CustomError(`Invalid Bitcoin address: ${address3}`);
  }
  return {
    type: "implicit",
    pkh: address3
  };
};
function bitcoinToSatoshi(amount) {
  const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (!match) {
    throw new CustomError(`Invalid Bitcoin amount: ${amount}`);
  }
  const [, neg, whole, frac = ""] = match;
  const sats = BigInt(whole) * SATS_PER_BTC + BigInt(frac.slice(0, BTC_DECIMALS).padEnd(BTC_DECIMALS, "0"));
  return neg === "-" ? -sats : sats;
}
function satoshiToBitcoin(sats) {
  const val = typeof sats === "string" ? BigInt(sats) : sats;
  const neg = val < 0n;
  const abs = neg ? -val : val;
  const whole = abs / SATS_PER_BTC;
  const frac = abs % SATS_PER_BTC;
  const fracStr = frac.toString().padStart(BTC_DECIMALS, "0");
  return `${neg ? "-" : ""}${whole.toString()}.${fracStr}`;
}
function formatBitcoinAmount(sats) {
  const btc = satoshiToBitcoin(sats.toString());
  return btc.includes(".") ? btc.replace(/\.?0+$/, "") : btc;
}
var getTxDirection = (tx, userAddress) => {
  const hasUserInput = tx.vin.some(
    (input) => input.prevout && input.prevout.scriptpubkey_address === userAddress
  );
  const hasUserOutput = tx.vout.some((output) => output.scriptpubkey_address === userAddress);
  if (hasUserInput && hasUserOutput) {
    return TransactionDirections.SEND;
  } else if (hasUserInput) {
    return TransactionDirections.SEND;
  } else if (hasUserOutput) {
    return TransactionDirections.RECEIVE;
  }
  return TransactionDirections.SEND;
};
var getTxAmount = (tx, userAddress) => {
  const direction = getTxDirection(tx, userAddress);
  if (direction === TransactionDirections.RECEIVE) {
    const receivedAmount = tx.vout.filter((output) => output.scriptpubkey_address === userAddress).reduce((sum, output) => sum + output.value, 0);
    return formatBitcoinAmount(receivedAmount);
  } else {
    const userInputs = tx.vin.filter((input) => input.prevout && input.prevout.scriptpubkey_address === userAddress).reduce((sum, input) => sum + (input.prevout?.value || 0), 0);
    const userOutputs = tx.vout.filter((output) => output.scriptpubkey_address === userAddress).reduce((sum, output) => sum + output.value, 0);
    const sentAmount = userInputs - userOutputs - tx.fee;
    return formatBitcoinAmount(Math.max(0, sentAmount));
  }
};
var getTxToFrom = (tx, userAddress) => {
  const direction = getTxDirection(tx, userAddress);
  if (direction === TransactionDirections.RECEIVE) {
    const fromAddress = tx.vin.find((input) => input.prevout && input.prevout.scriptpubkey_address !== userAddress)?.prevout?.scriptpubkey_address || "";
    return { from: fromAddress, to: userAddress };
  } else {
    const toAddress = tx.vout.find(
      (output) => output.scriptpubkey_address && output.scriptpubkey_address !== userAddress
    )?.scriptpubkey_address || "";
    return { to: toAddress, from: userAddress };
  }
};
var normalizeBitcoinTx = (chainId, { explorerUrl, symbol }) => (tx, userAddress) => {
  const txDirection = getTxDirection(tx, userAddress);
  const amount = getTxAmount(tx, userAddress);
  const { to, from } = getTxToFrom(tx, userAddress);
  const timestamp = tx.status.block_time ? new Date(tx.status.block_time * 1e3).toISOString() : (/* @__PURE__ */ new Date()).toISOString();
  return {
    hash: tx.txid,
    from,
    to,
    amount,
    timestamp,
    explorerUrl: `${explorerUrl}/tx/${tx.txid}`,
    status: tx.status.confirmed ? "confirmed" : "pending",
    fee: formatBitcoinAmount(tx.fee),
    chainId,
    id: tx.txid,
    operationType: OperationTypes.TRANSACTION,
    ticker: symbol,
    txDirection,
    isPending: !tx.status.confirmed
  };
};
var getAddressType = (address3) => {
  if (!isBitcoinAddressValid(address3)) {
    throw new CustomError(`Invalid Bitcoin address: ${address3}`);
  }
  const addr = address3.toLowerCase();
  if (addr.startsWith("bc1p") || addr.startsWith("tb1p")) {
    return BITCOIN_ADDRESS_TYPES.P2TR;
  }
  if (addr.startsWith("bc1q") || addr.startsWith("tb1q")) {
    return BITCOIN_ADDRESS_TYPES.P2WPKH;
  }
  if (addr.startsWith("3") || addr.startsWith("2")) {
    return BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH;
  }
  if (addr.startsWith("1") || addr.startsWith("m") || addr.startsWith("n")) {
    return BITCOIN_ADDRESS_TYPES.P2PKH;
  }
  throw new CustomError(`Unknown or unsupported address type: ${address3}`);
};
var getAddressTypeFromPath = (path) => {
  if (!path || typeof path !== "string") {
    throw new CustomError("Invalid derivation path");
  }
  const match = path.match(/^m\/(\d+)'/);
  if (!match) {
    throw new CustomError(`Invalid derivation path format: ${path}`);
  }
  const purpose = match[1];
  switch (purpose) {
    case PATH_PURPOSE.P2PKH:
      return BITCOIN_ADDRESS_TYPES.P2PKH;
    // Legacy
    case PATH_PURPOSE.P2SH_P2WPKH:
      return BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH;
    // Nested SegWit
    case PATH_PURPOSE.P2WPKH:
      return BITCOIN_ADDRESS_TYPES.P2WPKH;
    // Native SegWit
    case PATH_PURPOSE.P2TR:
      return BITCOIN_ADDRESS_TYPES.P2TR;
    // Taproot
    default:
      throw new CustomError(`Unknown or unsupported derivation path purpose: ${purpose}`);
  }
};
var getPayments = (pubkeyBuffer, network) => {
  const payments3 = {
    [BITCOIN_ADDRESS_TYPES.P2WPKH]: bitcoin.payments.p2wpkh({
      pubkey: pubkeyBuffer,
      network
    }),
    [BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH]: bitcoin.payments.p2sh({
      redeem: bitcoin.payments.p2wpkh({ pubkey: pubkeyBuffer, network }),
      network
    }),
    [BITCOIN_ADDRESS_TYPES.P2TR]: bitcoin.payments.p2tr({
      internalPubkey: pubkeyBuffer.subarray(1, 33),
      network
    }),
    [BITCOIN_ADDRESS_TYPES.P2PKH]: bitcoin.payments.p2pkh({
      pubkey: pubkeyBuffer,
      network
    })
  };
  return payments3;
};

// src/providers/bitcoin/utils/selection.ts
var VBYTES = {
  [BITCOIN_ADDRESS_TYPES.P2TR]: { in: 57.5, out: 43 },
  // Taproot
  [BITCOIN_ADDRESS_TYPES.P2WPKH]: { in: 68, out: 31 },
  // Native SegWit
  [BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH]: { in: 91, out: 32 },
  // Nested SegWit
  [BITCOIN_ADDRESS_TYPES.P2PKH]: { in: 148, out: 34 }
  // Legacy
};
var DUST_P2WPKH = 294;
var OVERHEAD_VBYTES = 10;
function selectUtxosAndOutputs(utxos, targetAddress, targetValue, feeRate, addressType) {
  const targets = [{ address: targetAddress, value: targetValue }];
  const { inputs, outputs, fee } = coinselect(utxos, targets, feeRate);
  if (inputs && outputs) {
    return { inputs, outputs, fee: fee || 0 };
  }
  const utxosSorted = [...utxos].sort((a, b) => b.value - a.value);
  const inV = VBYTES[addressType].in;
  const outV = VBYTES[addressType].out;
  let totalValue = 0;
  const selectedInputs = [];
  for (const u of utxosSorted) {
    selectedInputs.push(u);
    totalValue += u.value;
    const inputsWeight = selectedInputs.length * inV;
    let totalBytesWeight = OVERHEAD_VBYTES + inputsWeight + 2 * outV;
    let approximateFee = Math.ceil(totalBytesWeight * feeRate);
    const change = totalValue - targetValue - approximateFee;
    if (change >= DUST_P2WPKH) {
      return {
        inputs: selectedInputs,
        outputs: [{ address: targetAddress, value: targetValue }, { value: change }],
        fee: approximateFee
      };
    }
    totalBytesWeight = OVERHEAD_VBYTES + inputsWeight + outV;
    approximateFee = Math.ceil(totalBytesWeight * feeRate);
    if (totalValue >= targetValue + approximateFee) {
      return {
        inputs: selectedInputs,
        outputs: [{ address: targetAddress, value: targetValue }],
        fee: approximateFee
      };
    }
  }
  throw new CustomError2("Insufficient funds for the transaction");
}

// src/providers/bitcoin/utils/taproot.ts
import * as secp256k1 from "@bitcoinerlab/secp256k1";
import { crypto } from "bitcoinjs-lib";
var createTaprootSigner = (keyPair, network) => {
  if (!keyPair.privateKey) {
    throw new Error("Missing private key");
  }
  const xOnlyPubKey = toXOnly(keyPair.publicKey);
  const tweak = crypto.taggedHash("TapTweak", Buffer.concat([xOnlyPubKey, Buffer.alloc(0)]));
  const tweakedPubKeyResult = secp256k1.xOnlyPointAddTweak(xOnlyPubKey, tweak);
  if (!tweakedPubKeyResult) {
    throw new Error("Failed to compute tweaked public key");
  }
  const isInternalOdd = keyPair.publicKey[0] === 3;
  const adjustedPrivateKey = isInternalOdd ? secp256k1.privateNegate(keyPair.privateKey) : keyPair.privateKey;
  const tweakedPrivateKey = secp256k1.privateAdd(adjustedPrivateKey, tweak);
  if (!tweakedPrivateKey) {
    throw new Error("Failed to compute tweaked private key");
  }
  const derivedTweakedPubkey = secp256k1.xOnlyPointFromScalar(tweakedPrivateKey);
  if (!Buffer.from(derivedTweakedPubkey).equals(Buffer.from(tweakedPubKeyResult.xOnlyPubkey))) {
    throw new Error("Invalid taproot key tweak");
  }
  return {
    publicKey: Buffer.from(tweakedPubKeyResult.xOnlyPubkey),
    network,
    sign: (hash) => Buffer.from(secp256k1.signSchnorr(hash, tweakedPrivateKey)),
    signSchnorr: (hash) => Buffer.from(secp256k1.signSchnorr(hash, tweakedPrivateKey))
  };
};
function toXOnly(pubkey) {
  return pubkey.subarray(1, 33);
}
var signAllTaprootInputs = ({
  psbt,
  keyPair,
  network
}) => {
  const signer = createTaprootSigner(keyPair, network);
  for (let i = 0; i < psbt.inputCount; i++) {
    psbt.signTaprootInput(i, signer);
  }
};

// src/providers/bitcoin/config.ts
var commonConfig = {
  namespace: ChainNamespaces.BITCOIN,
  symbol: "BTC",
  buyTezUrl: "",
  tzktApiUrl: "",
  chain: ChainIds.BITCOIN,
  decimals: BTC_DECIMALS
};
var bitcoinConfig = {
  // Mainnet
  mainnet: {
    name: "Bitcoin",
    identifier: "0",
    rpcUrl: "",
    explorerUrl: `${PRICING_API_URL}/blockstream/mainnet`,
    ...commonConfig
  },
  // Testnet
  testnet: {
    name: "Bitcoin Testnet",
    identifier: "1",
    rpcUrl: "",
    explorerUrl: `${PRICING_API_URL}/blockstream/testnet`,
    ...commonConfig
  }
};
var defaultDerivationPath = {
  0: "m/84'/0'/0'/0/0",
  1: "m/84'/1'/0'/0/0"
};
var derivationPathTemplate = "m/84'/0'/?'/0/0";
var btcDerivationPathTemplates = {
  0: "m/84'/0'/?'/0/0",
  // Mainnet
  1: "m/84'/1'/?'/0/0"
  // Testnet
};

// src/providers/evm/utils.ts
import { CustomError as CustomError3, getEnv as getEnv2 } from "@umami/utils";
import { differenceInDays, format, formatDistance } from "date-fns";
import { ethers, formatUnits } from "ethers";
var DEFAULT_ETHERS_DECIMALS = 18;
var isEvmAddressValid = (address3) => ethers.isAddress(address3);
var formatEthersAmount = (amount, decimals = DEFAULT_ETHERS_DECIMALS) => {
  const value = formatUnits(amount, decimals);
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6
  });
  return formatter.format(Number(value));
};
var transactionTypes = {
  token_transfer: OperationTypes.TOKEN_TRANSFER,
  coin_transfer: OperationTypes.TRANSACTION,
  contract_call: OperationTypes.CONTRACT_CALL
};
var normalizeTimestamp = (timestamp = (/* @__PURE__ */ new Date()).toISOString(), { isShort = false } = {}) => {
  const currentDate = /* @__PURE__ */ new Date();
  const timestampDate = new Date(timestamp);
  const dayDifference = differenceInDays(currentDate, timestamp);
  const isSameYear = currentDate.getFullYear() === timestampDate.getFullYear();
  if (isShort) {
    if (dayDifference < 1) {
      return format(timestampDate, "HH:mm");
    }
    return format(timestampDate, "dd/MM");
  }
  if (dayDifference < 2) {
    return formatDistance(timestamp, currentDate, { addSuffix: true });
  } else if (isSameYear) {
    return format(timestamp, "dd MMM");
  } else {
    return format(timestamp, "dd MMM yyyy");
  }
};
var getTransactionDirection = (transaction, address3) => {
  if (transaction.from.hash === address3) {
    return TransactionDirections.SEND;
  } else if (transaction.to.hash === address3) {
    return TransactionDirections.RECEIVE;
  }
  return TransactionDirections.SEND;
};
var parseEvmAddress = (address3) => {
  if (!ethers.isAddress(address3)) {
    throw new CustomError3(`Invalid evm address: ${address3}`);
  }
  return { type: "implicit", pkh: address3 };
};
var normalizeEvmTx = (chainId, { explorerUrl, symbol }) => (tx, address3) => {
  const isContractCall = tx.transaction_types.includes("contract_call");
  const ticker = tx.transaction_types.includes("coin_transfer") ? symbol : void 0;
  return {
    hash: tx.hash,
    from: tx.from.hash,
    to: tx.to.hash,
    amount: formatEthersAmount(tx.value),
    timestamp: tx.timestamp,
    explorerUrl: `${explorerUrl}/tx/${tx.hash}`,
    status: tx.status,
    fee: formatEthersAmount(tx.fee.value),
    chainId,
    id: tx.hash,
    txDirection: getTransactionDirection(tx, address3),
    operationType: OperationTypes.TRANSACTION,
    ticker,
    isContractCall
  };
};
var normalizeEvmNFT = (chainId) => (nft) => {
  const { token, id, value, token_type, metadata, image_url, media_url, animation_url } = nft;
  const defaultName = `${token.symbol || "NFT"} #${id}`;
  const name = metadata?.name || token.name || defaultName;
  const imageUrl = image_url || media_url || metadata?.image || token.icon_url;
  const animationUrl = animation_url || metadata?.animation_url;
  const attributes = metadata?.attributes?.map((attr) => ({
    name: attr.trait_type,
    value: attr.value
  }));
  const tokenAddress = token.address_hash || `unknown-${token.symbol || "NFT"}`;
  return {
    id: `${tokenAddress}-${id}`,
    address: tokenAddress,
    balance: value,
    chainId,
    type: token_type,
    imageUrl,
    name,
    token,
    animationUrl,
    tokenId: id,
    metadata: {
      ...metadata ?? {},
      attributes
    }
  };
};
var normalizeEvmToken = (chainId) => (tokenBalance) => {
  const { token } = tokenBalance;
  const decimals = parseInt(token.decimals, 10);
  const balance = formatUnits(tokenBalance.value, decimals);
  const tokenAddress = token.address_hash || `unknown-${token.symbol}`;
  return {
    id: `${tokenAddress}-${tokenBalance.token_id || "0"}`,
    address: tokenAddress,
    name: token.name,
    symbol: token.symbol,
    decimals,
    balance,
    value: tokenBalance.value,
    chainId,
    type: token.type,
    iconUrl: token.icon_url
  };
};
var getIconUrl = (token) => {
  if (!token) {
    return void 0;
  }
  if ("image_url" in token) {
    return token.image_url;
  }
  if ("media_url" in token) {
    return token.media_url;
  }
  if ("icon_url" in token) {
    return token.icon_url;
  }
  return token.metadata?.image;
};
var normalizeEvmTokenTransfer = (chainId, { explorerUrl }) => (tx, address3) => {
  const { transaction_hash, from, to, total, timestamp, type, token: rawToken } = tx;
  const amount = "value" in total ? formatUnits(total.value, parseInt(total.decimals ?? "0", 10)) : "1";
  const token = "token_instance" in tx.total ? tx.total.token_instance : tx.token;
  const ticker = token && "metadata" in token && token.metadata && "name" in token.metadata ? token.metadata.name : rawToken.symbol;
  const iconUrl = getIconUrl(token);
  return {
    hash: transaction_hash,
    from: from.hash,
    to: to.hash,
    amount,
    timestamp,
    explorerUrl: `${explorerUrl}/tx/${transaction_hash}`,
    chainId,
    id: transaction_hash,
    txDirection: getTransactionDirection(tx, address3),
    operationType: type,
    ticker,
    token,
    iconUrl,
    isNft: rawToken.type === "ERC-721" || rawToken.type === "ERC-1155"
  };
};
var evmDerivationPathTemplate = "m/44'/60'/?'/0/0";
var getChainConfig = (config, envKey, chainName) => {
  const configKey = getEnv2(envKey);
  if (!(configKey in config)) {
    throw new Error(`Invalid ${chainName} network config: ${String(configKey)}.`);
  }
  return config[configKey];
};

// src/providers/evm/bsc/config.ts
var commonConfig2 = {
  namespace: ChainNamespaces.EVM,
  symbol: "BNB",
  buyTezUrl: "",
  tzktApiUrl: "",
  chain: ChainIds.BSC,
  decimals: DEFAULT_ETHERS_DECIMALS
};
var bscConfig = {
  // Mainnet
  mainnet: {
    name: "BSC",
    identifier: "56",
    rpcUrl: "https://binance.llamarpc.com",
    explorerUrl: "https://bscscan.com",
    ...commonConfig2
  },
  // Testnet
  testnet: {
    name: "BSC Testnet",
    identifier: "97",
    rpcUrl: "https://bsc-testnet.publicnode.com",
    explorerUrl: "https://testnet.bscscan.com",
    isTestnet: true,
    ...commonConfig2
  }
};

// src/providers/evm/ethereum/config.ts
var commonConfig3 = {
  namespace: ChainNamespaces.EVM,
  symbol: "ETH",
  buyTezUrl: "",
  tzktApiUrl: "",
  chain: ChainIds.ETHEREUM,
  decimals: DEFAULT_ETHERS_DECIMALS
};
var ethereumConfig = {
  // Mainnet
  // Infura seems to be the more private reliable provider for mainnet (you would need to register to infura and get an api key)
  mainnet_infura: {
    name: "Ethereum",
    rpcUrl: `${PRICING_API_URL}/infura/mainnet`,
    identifier: "1",
    explorerUrl: "https://eth.blockscout.com",
    explorerProxyUrl: `${PRICING_API_URL}/blockscout/mainnet`,
    ...commonConfig3
  },
  mainnet_blockscout: {
    name: "Ethereum",
    rpcUrl: "https://eth.blockscout.com/api/eth-rpc",
    identifier: "1",
    explorerUrl: "https://eth.blockscout.com",
    explorerProxyUrl: `${PRICING_API_URL}/blockscout/mainnet`,
    ...commonConfig3
  },
  // Testnet
  // Infura seems to be the more private reliable provider for sepolia (you would need to register to infura and get an api key)
  testnet_infura_sepolia: {
    name: "Ethereum Sepolia",
    rpcUrl: `${PRICING_API_URL}/infura/sepolia`,
    identifier: "11155111",
    explorerUrl: "https://eth-sepolia.blockscout.com",
    explorerProxyUrl: `${PRICING_API_URL}/blockscout/sepolia`,
    isTestnet: true,
    ...commonConfig3
  },
  // Onfinality seems to be the more public reliable provider for sepolia
  testnet_onfinality_sepolia: {
    name: "Ethereum Sepolia",
    identifier: "11155111",
    rpcUrl: "https://eth-sepolia.api.onfinality.io/public",
    explorerUrl: "https://eth-sepolia.blockscout.com",
    explorerProxyUrl: `${PRICING_API_URL}/blockscout/sepolia`,
    isTestnet: true,
    ...commonConfig3
  },
  testnet_blockscout_sepolia: {
    name: "Ethereum Sepolia",
    identifier: "11155111",
    rpcUrl: "https://eth-sepolia.blockscout.com/api/eth-rpc",
    explorerUrl: "https://eth-sepolia.blockscout.com",
    explorerProxyUrl: `${PRICING_API_URL}/blockscout/sepolia`,
    isTestnet: true,
    ...commonConfig3
  },
  testnet_blockscout_holesky: {
    name: "Ethereum Holesky",
    identifier: "17000",
    rpcUrl: "https://ethereum-holesky-rpc.publicnode.com",
    explorerUrl: "https://eth-holesky.blockscout.com",
    explorerProxyUrl: `${PRICING_API_URL}/blockscout/holesky`,
    isTestnet: true,
    ...commonConfig3
  }
};

// src/providers/evm/etherlink/config.ts
var commonConfig4 = {
  namespace: ChainNamespaces.EVM,
  symbol: "XTZ",
  buyTezUrl: "",
  tzktApiUrl: "",
  chain: ChainIds.ETHERLINK,
  decimals: 8
};
var etherlinkConfig = {
  mainnet: {
    name: "Etherlink",
    identifier: "42793",
    rpcUrl: "https://node.mainnet.etherlink.com",
    explorerUrl: "https://explorer.etherlink.com",
    ...commonConfig4
  },
  testnet: {
    name: "Etherlink Shadownet",
    identifier: "127823",
    rpcUrl: "https://node.shadownet.etherlink.com",
    explorerUrl: "https://shadownet.explorer.etherlink.com",
    isTestnet: true,
    ...commonConfig4
  }
};

// src/providers/tezos/constants.ts
var TEZ = "XTZ";
var TEZ_DECIMALS = 6;
var BLOCK_TIME = 10 * 1e3;
var POLLING_INTERVAL = 60 * 1e3;
var POLLING_STALE_TIME = POLLING_INTERVAL - 5 * 1e3;
var verifiedTokens = [
  "KT1XnTn74bUtxHfDtBmm2bGZAQfhPbvKWR8o",
  "KT1PWx2mnDueood7fEmfbBDKx1D9BAnnXitn",
  "KT1XRPEPXbZK25r3Htzp2o1x7xdMMmfocKNW",
  "KT1XRPEPXbZK25r3Htzp2o1x7xdMMmfocKNW",
  "KT1XRPEPXbZK25r3Htzp2o1x7xdMMmfocKNW",
  "KT1K9gCRgaLRFKTErYt1wVxA3Frb9FjasjTV",
  "KT1Ha4yFVeyzw6KRAdkzq6TxDHB97KG4pZe8",
  "KT1JBNFcB5tiycHNdYGYCtR3kk6JaJysUCi8",
  "KT1Xobej4mc6XgEjDoJoHtTKgbD1ELMvcQuL",
  "KT1JVjgXPMMSaa6FkzeJcgb8q9cUaLmwaJUX",
  "KT193D4vozYnhGJQVtw7CoxxqphqUEEwK6Vb",
  "KT1LN4LPSqTMS7Sd2CJw4bbDGRkMv2t68Fy9",
  "KT1914CUZ7EegAFPbfgQMRkw8Uz5mYkEz2ui",
  "KT1UsSfaXyqcjSVPeiD7U1bWgKy3taYN7NWY",
  "KT19ovJhcsUn4YU8Q5L3BGovKSixfbWcecEA",
  "KT1TgmD7kXQzofpuc9VbTRMdZCS2e6JDuTtc",
  "KT1F1mn2jbqQCJcsNgYKVAQjvenecNMY2oPK",
  "KT1ErKVqEhG9jxXgUG2KGLW3bNM7zXHX8SDF",
  "KT1A5P4ejnLix13jtadsfV9GCnXLMNnab8UT",
  "KT1AM3PV1cwmGRw28DVTgsjjsjHvmL6z4rGh",
  "KT1UsSfaXyqcjSVPeiD7U1bWgKy3taYN7NWY",
  "KT1SjXiUX63QvdNMcM2m492f7kuf8JxXRLp4",
  "KT1MZg99PxMDEENwB4Fi64xkqAVh5d1rv8Z9",
  "KT1UsSfaXyqcjSVPeiD7U1bWgKy3taYN7NWY",
  "KT1XTxpQvo7oRCqp85LikEZgAZ22uDxhbWJv",
  "KT1VaEsVNiBoA56eToEK6n6BcPgh1tdx9eXi"
];

// src/providers/tezos/Network.ts
var commonConfig5 = {
  namespace: ChainNamespaces.TEZOS,
  symbol: "XTZ",
  chain: ChainIds.TEZOS,
  decimals: TEZ_DECIMALS
};
var MAINNET = {
  name: "mainnet",
  rpcUrl: process.env.TEZOS_MAINNET_RPC_URL ?? "https://tezos-mainnet.octez.io",
  tzktApiUrl: process.env.TEZOS_MAINNET_TZKT_API_URL ?? "https://api.mainnet.tzkt.io",
  explorerUrl: "https://tzkt.io",
  buyTezUrl: "https://widget.wert.io",
  identifier: "mainnet",
  saplingContractAddress: "KT1KzAPQdpziH3bxxJXQNmNQA46oo8tnDQfj",
  ...commonConfig5
};
var SHADOWNET = {
  name: "shadownet",
  rpcUrl: process.env.TEZOS_SHADOWNET_RPC_URL ?? "https://rpc.shadownet.teztnets.com",
  tzktApiUrl: process.env.TEZOS_SHADOWNET_TZKT_API_URL ?? "https://api.shadownet.tzkt.io",
  explorerUrl: "https://shadownet.tzkt.io",
  buyTezUrl: "https://faucet.shadownet.teztnets.com",
  identifier: "shadownet",
  isTestnet: true,
  ...commonConfig5
};
var MICHELSON = {
  name: "michelson",
  rpcUrl: process.env.TEZOS_MICHELSON_RPC_URL ?? "https://michelson.etherlink.mainnet.octez.io",
  tzktApiUrl: process.env.TEZOS_MICHELSON_TZKT_API_URL ?? "https://api.etherlink.tzkt.io",
  explorerUrl: "https://etherlink.tzkt.io",
  buyTezUrl: "",
  identifier: "michelson",
  genesisChainId: "NetXohUVN5QWR4f",
  ...commonConfig5
};
var MICHELSON_SHADOWNET = {
  name: "michelson shadownet",
  rpcUrl: process.env.TEZOS_MICHELSON_SHADOWNET_RPC_URL ?? "https://michelson.etherlink.shadownet.octez.io",
  tzktApiUrl: process.env.TEZOS_MICHELSON_SHADOWNET_TZKT_API_URL ?? "https://api.shadownet.etherlink.tzkt.io",
  explorerUrl: "https://shadownet.etherlink.tzkt.io",
  buyTezUrl: "https://faucet.shadownet.teztnets.com",
  identifier: "michelson-shadownet",
  isTestnet: true,
  ...commonConfig5
};
var PREVIEWNET = {
  name: "previewnet",
  rpcUrl: process.env.TEZOS_PREVIEWNET_RPC_URL ?? "https://michelson.previewnet.tezosx.nomadic-labs.com",
  tzktApiUrl: process.env.TEZOS_PREVIEWNET_TZKT_API_URL ?? "https://api.previewnet.tezosx.tzkt.io",
  explorerUrl: "https://previewnet.tezosx.tzkt.io",
  buyTezUrl: "https://faucet.previewnet.tezosx.nomadic-labs.com",
  identifier: "previewnet",
  genesisChainId: "NetXY2oPPzkxUW1",
  isTestnet: true,
  ...commonConfig5
};
var isDefault = (network) => !!DefaultNetworks.find((n) => n.name === network.name);
var DefaultNetworks = [
  MAINNET,
  SHADOWNET,
  MICHELSON,
  MICHELSON_SHADOWNET,
  PREVIEWNET
];

// src/providers/networks.ts
var ChainFullIds = generateChainFullIdsFromConfigs([
  MAINNET,
  SHADOWNET,
  MICHELSON,
  MICHELSON_SHADOWNET,
  PREVIEWNET,
  ...Object.values(ethereumConfig),
  ...Object.values(etherlinkConfig),
  ...Object.values(bscConfig),
  ...Object.values(bitcoinConfig)
]);
var NETWORK_CONFIGS = {
  [ChainFullIds.ETHEREUM]: ethereumConfig.mainnet_infura,
  [ChainFullIds.ETHEREUM_SEPOLIA]: ethereumConfig.testnet_infura_sepolia,
  [ChainFullIds.ETHERLINK]: etherlinkConfig.mainnet,
  [ChainFullIds.ETHERLINK_SHADOWNET]: etherlinkConfig.testnet,
  [ChainFullIds.TEZOS]: MAINNET,
  [ChainFullIds.TEZOS_SHADOWNET]: SHADOWNET,
  [ChainFullIds.TEZOS_MICHELSON]: MICHELSON,
  [ChainFullIds.TEZOS_MICHELSON_SHADOWNET]: MICHELSON_SHADOWNET,
  [ChainFullIds.TEZOS_PREVIEWNET]: PREVIEWNET,
  [ChainFullIds.BITCOIN]: bitcoinConfig.mainnet,
  [ChainFullIds.BITCOIN_TESTNET]: bitcoinConfig.testnet
  // TODO: add BSC support
  // [ChainFullIds.BSC_MAINNET]: networks.bsc.mainnet[0],
  // [ChainFullIds.BSC_TESTNET]: networks.bsc.testnet[0],
};
var networks2 = {
  [ChainIds.ETHEREUM]: {
    mainnet: [ethereumConfig.mainnet_infura],
    testnet: [ethereumConfig.testnet_infura_sepolia]
  },
  [ChainIds.TEZOS]: {
    mainnet: [MAINNET, MICHELSON],
    testnet: [SHADOWNET, MICHELSON_SHADOWNET, PREVIEWNET]
  },
  [ChainIds.ETHERLINK]: {
    mainnet: [etherlinkConfig.mainnet],
    testnet: [etherlinkConfig.testnet]
  },
  [ChainIds.BSC]: {
    mainnet: [bscConfig.mainnet],
    testnet: [bscConfig.testnet]
  },
  [ChainIds.BITCOIN]: {
    mainnet: [bitcoinConfig.mainnet],
    testnet: [bitcoinConfig.testnet]
  }
};

// src/utils/typeguards.ts
var isChainFullId = (chainFullId) => Object.values(ChainFullIds).includes(chainFullId);
var isChainNamespace = (namespace) => Object.values(ChainNamespaces).includes(namespace);
var isChainId = (chainId) => Object.values(ChainIds).includes(chainId);
var isMultichainChainNamespace = (namespace) => Object.values(ChainNamespaces).includes(namespace);
var isDeprecated = (chainId) => DEPRECATED_CHAINS.includes(chainId);

// src/utils/chains.ts
var parseChainFullId = (chainFullId) => {
  const [namespace, identifier] = chainFullId.split(":");
  if (!isChainNamespace(namespace)) {
    throw new CustomError4(`Invalid chain namespace: ${namespace}`);
  }
  return { namespace, identifier };
};
var getChainData = (accounts) => accounts.reduce((acc, account) => {
  acc[account.chainId] = {
    address: account.address,
    pk: account.pk,
    derivationPath: account.derivationPath,
    derivationPathTemplate: account.derivationPathTemplate,
    curve: account.curve,
    decimals: account.decimals
  };
  return acc;
}, {});
var prepareChainTransaction = (transaction) => ({
  to: transaction.recipient?.pkh || transaction.to,
  value: transaction.amount?.toString() || transaction.value?.toString() || "0",
  contract: transaction.contract?.pkh || transaction.contract,
  tokenId: transaction.tokenId,
  tokenType: transaction.tokenType,
  data: transaction.data
});
var getHash = (operation) => {
  if (operation.opHash) {
    return operation.opHash;
  }
  return operation.hash;
};
var DEFAULT_CHAIN_PRIORITY = 3;
var ChainsPriority = {
  [ChainIds.TEZOS]: 0,
  [ChainIds.ETHERLINK]: 1,
  [ChainIds.ETHEREUM]: 2
};
var getChainPriority = (chainId) => ChainsPriority[chainId] ?? DEFAULT_CHAIN_PRIORITY;
var getNativeTokenPriority = (chainId, symbol) => CryptoSymbols[chainId] === symbol ? 0 : 1;

// src/providers/tezos/Address.ts
import {
  PrefixV2,
  ValidationResult,
  b58DecodeAndCheckPrefix,
  validateAddress
} from "@taquito/utils";
import { CustomError as CustomError5 } from "@umami/utils";
var isSaplingAddress = (pkh) => {
  if (!pkh) {
    return false;
  }
  try {
    b58DecodeAndCheckPrefix(pkh, [PrefixV2.SaplingAddress]);
    return true;
  } catch {
    return false;
  }
};
var parsePkh = (pkh) => {
  if (isSaplingAddress(pkh)) {
    return parseSaplingPkh(pkh);
  }
  if (isValidContractPkh(pkh)) {
    return parseContractPkh(pkh);
  }
  if (isValidImplicitPkh(pkh)) {
    return parseImplicitPkh(pkh);
  }
  if (isValidSmartRollupPkh(pkh)) {
    return parseSmartRollupPkh(pkh);
  }
  throw new CustomError5(`Cannot parse address type: ${pkh}`);
};
var isAddressValid = (pkh) => validateAddress(pkh) === ValidationResult.VALID;
var isValidContractPkh = (pkh) => isAddressValid(pkh) && !!pkh.match(/^KT1\w+/);
var isValidImplicitPkh = (pkh) => isAddressValid(pkh) && !!pkh.match(/^tz[1234]\w+/);
var isValidSmartRollupPkh = (pkh) => isAddressValid(pkh) && !!pkh.match(/^sr1\w+/);
var parseSaplingPkh = (pkh) => {
  if (isSaplingAddress(pkh)) {
    return { type: "sapling", pkh };
  }
  throw new CustomError5(`Invalid sapling address: ${pkh}`);
};
var parseContractPkh = (pkh) => {
  if (isValidContractPkh(pkh)) {
    return { type: "contract", pkh };
  }
  throw new CustomError5(`Invalid contract address: ${pkh}`);
};
var parseImplicitPkh = (pkh) => {
  if (isValidImplicitPkh(pkh)) {
    return { type: "implicit", pkh };
  }
  throw new CustomError5(`Invalid implicit address: ${pkh}`);
};
var parseSmartRollupPkh = (pkh) => {
  if (isValidSmartRollupPkh(pkh)) {
    return { type: "smart_rollup", pkh };
  }
  throw new CustomError5(`Invalid smart rollup address: ${pkh}`);
};

// src/providers/tezos/derivationPathUtils.ts
var defaultDerivationPathTemplate = "44'/1729'/?'/0'";
var DEFAULT_DERIVATION_PATH_TEMPLATE = {
  label: "Default - m/44'/1729'/?'/0'",
  value: defaultDerivationPathTemplate
};
var AVAILABLE_DERIVATION_PATH_TEMPLATES = [
  DEFAULT_DERIVATION_PATH_TEMPLATE,
  { label: "m/44'/1729'/0'/?'", value: "44'/1729'/0'/?'" },
  { label: "m/44'/1729'/?'/0'/0'", value: "44'/1729'/?'/0'/0'" },
  { label: "m/44'/1729'/0'/0'/?'", value: "44'/1729'/0'/0'/?'" }
];
var getDefaultDerivationPath = (index) => makeDerivationPath(defaultDerivationPathTemplate, index);
var makeDerivationPath = (template, index = 0, currentPath) => {
  if (!currentPath || currentPath === template) {
    return template.replace("?", index.toString());
  } else {
    return currentPath;
  }
};
var getDerivationPathNextIndex = (derivationPath, derivationPathTemplate2) => {
  const currentPathParts = derivationPath.split("/");
  const templateParts = derivationPathTemplate2.split("/");
  const index = templateParts.findLastIndex((part) => part.includes("?"));
  const currentValueNumber = parseInt(currentPathParts[index]);
  return currentValueNumber + 1;
};

// src/providers/tezos/fakeSigner.ts
var FakeSigner = class {
  pk;
  pkh;
  constructor(pk, pkh) {
    this.pk = pk;
    this.pkh = pkh;
  }
  publicKey() {
    return Promise.resolve(this.pk);
  }
  publicKeyHash() {
    return Promise.resolve(this.pkh);
  }
  sign() {
    return Promise.reject(new Error("`sign` method not available"));
  }
  secretKey() {
    return Promise.reject(new Error("empty secret key"));
  }
};

// src/providers/tezos/fetch.ts
import { RpcClient } from "@taquito/rpc";
var getProtocolSettings = (network) => new RpcClient(network.rpcUrl).getConstants();
var isAccountRevealed = async (pkh, network) => {
  const url = `${network.tzktApiUrl}/v1/accounts/${pkh}?select.fields=type,revealed`;
  const { type, revealed } = await fetch(url).then((res) => res.json());
  return type !== "empty" && revealed;
};

// src/providers/tezos/format.ts
import { format as format2 } from "@taquito/utils";
import { BigNumber } from "bignumber.js";
var truncate = (name, len) => name.length > len ? name.slice(0, len - 3) + "..." : name;
var tezToMutez = (tez) => {
  const result = format2("tz", "mutez", tez);
  return new BigNumber(result.toString());
};
var mutezToTez = (mutez) => {
  const result = format2("mutez", "tz", mutez);
  return new BigNumber(result.toString());
};
var formatTezAmount = (mutez, minimumFractionDigits) => {
  const tezAmount = BigNumber(mutezToTez(mutez)).toNumber();
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: minimumFractionDigits ?? TEZ_DECIMALS,
    maximumFractionDigits: TEZ_DECIMALS
  });
  return formatter.format(tezAmount);
};
var prettyTezAmount = (mutez) => {
  const fee = formatTezAmount(mutez);
  return `${fee} ${TEZ}`;
};
var formatTezAmountMin0Decimals = (mutez) => {
  const fee = formatTezAmount(mutez, 0);
  return `${fee} ${TEZ}`;
};
var formatUsdAmount = (usd) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  });
  return formatter.format(BigNumber(usd).toNumber());
};
var formatPkh = (pkh, isSapling) => {
  if (isSapling) {
    return `${pkh.slice(0, 18)}...${pkh.slice(-21)}`;
  }
  return `${pkh.slice(0, 5)}...${pkh.slice(-5)}`;
};

// src/providers/tezos/helpers.ts
import crypto2 from "crypto";
import TransportWebUSB from "@ledgerhq/hw-transport-webusb";
import { DerivationType, LedgerSigner } from "@taquito/ledger-signer";
import { Parser } from "@taquito/michel-codec";
import { InMemorySigner } from "@taquito/signer";
import { TezosToolkit } from "@taquito/taquito";
import { CustomError as CustomError6 } from "@umami/utils";
var generateHash = () => {
  const utf8 = new TextEncoder().encode(Date.now().toString());
  const hash = crypto2.createHash("sha256").update(utf8).digest("hex");
  return hash.slice(0, 8);
};
var curveToDerivationType = (curve) => {
  switch (curve) {
    case "ed25519":
      return DerivationType.ED25519;
    case "secp256k1":
      return DerivationType.SECP256K1;
    case "p256":
      return DerivationType.P256;
    case "bip25519":
      throw new CustomError6("bip25519 is not supported in Tezos");
  }
};
var makeSigner = async (config) => {
  switch (config.type) {
    case "social":
    case "mnemonic":
    case "secret_key":
      return new InMemorySigner(config.secretKey);
    case "ledger": {
      const devices = await TransportWebUSB.list();
      for (let i = 0; i < devices.length; i++) {
        devices[i].close();
      }
      const transport = await TransportWebUSB.create();
      const signer = new LedgerSigner(
        transport,
        config.account.derivationPath,
        false,
        // PK Verification not needed
        curveToDerivationType(config.account.curve)
      );
      return signer;
    }
    case "fake":
      return new FakeSigner(config.signer.pk, config.signer.address.pkh);
  }
};
var makeToolkit = async (config) => {
  const toolkit = new TezosToolkit(config.network.rpcUrl);
  const signer = await makeSigner(config);
  toolkit.setSignerProvider(signer);
  return toolkit;
};
var getPublicKeyPairFromSk = async (sk) => {
  const signer = new InMemorySigner(sk);
  return { pk: await signer.publicKey(), pkh: await signer.publicKeyHash() };
};
var derivePublicKeyPair = async (mnemonic, derivationPath, curve) => deriveSecretKey(mnemonic, derivationPath, curve).then(getPublicKeyPairFromSk);
var deriveSecretKey = (mnemonic, derivationPath, curve) => InMemorySigner.fromMnemonic({
  mnemonic,
  derivationPath,
  curve
}).secretKey();
var isValidMichelson = (object) => {
  try {
    new Parser().parseJSON(object);
    return true;
  } catch {
    return false;
  }
};
var getLedgerPublicKeyPair = async (derivationPath, curve) => {
  const transport = await TransportWebUSB.create();
  const ledgerSigner = new LedgerSigner(
    transport,
    derivationPath,
    true,
    curveToDerivationType(curve)
  );
  const pk = await ledgerSigner.publicKey();
  const pkh = await ledgerSigner.publicKeyHash();
  await transport.close();
  return { pk, pkh };
};
var getIPFSurl = (ipfsPath) => {
  if (!ipfsPath) {
    return void 0;
  }
  if (ipfsPath.startsWith("ipfs://")) {
    return ipfsPath.replace("ipfs://", "https://ipfs.io/ipfs/");
  }
  return ipfsPath;
};
var decryptSecretKey = async (secretKey, password) => {
  try {
    const signer = await InMemorySigner.fromSecretKey(secretKey.trim(), password);
    return await signer.secretKey();
  } catch (error) {
    const message = error.message || "";
    if (message.includes("can't decrypt secret key")) {
      throw new CustomError6("Key-password pair is invalid");
    }
    if (message.includes("Invalid private key")) {
      throw new CustomError6("Invalid secret key: checksum doesn't match");
    }
    throw new CustomError6(error.message);
  }
};

// src/providers/tezos/sapling.ts
import { InMemorySpendingKey } from "@taquito/sapling";
var generateKeys = async (mnemonic) => {
  const spendingKey = await InMemorySpendingKey.fromMnemonic(mnemonic);
  const viewingKeyProvider = await spendingKey.getSaplingViewingKeyProvider();
  const viewingKey = viewingKeyProvider.getFullViewingKey();
  const viewingKeyAddress = (await viewingKeyProvider.getAddress()).address;
  return { spendingKey, viewingKey, viewingKeyAddress };
};

// src/providers/tezos/testUtils.ts
var validSmartRollupAddresses = ["sr1Ghq66tYK9y3r8CC1Tf8i8m5nxh8nTvZEf"];
var mockSmartRollupAddress = (index) => ({
  type: "smart_rollup",
  pkh: validSmartRollupAddresses[index]
});
var validContractAddresses = [
  "KT1QuofAgnsWffHzLA7D78rxytJruGHDe7XG",
  "KT1CSKPf2jeLpMmrgKquN2bCjBTkAcAdRVDy",
  "KT1EctCuorV2NfVb1XTQgvzJ88MQtWP8cMMv",
  "KT1Ex8LrDbCrZuTgmWin8eEo7HFw74jAqTvz"
];
var mockContractAddress = (index) => ({
  type: "contract",
  pkh: validContractAddresses[index]
});
var validMockPkhs = [
  "tz1gUNyn3hmnEWqkusWPzxRaon1cs7ndWh7h",
  "tz1UZFB9kGauB6F5c2gfJo4hVcvrD8MeJ3Vf",
  "tz1ikfEcj3LmsmxpcC1RMZNzBHbEmybCc43D",
  "tz1g7Vk9dxDALJUp4w1UTnC41ssvRa7Q4XyS",
  "tz1i9imTXjMAW5HP5g3wq55Pcr43tDz8c3VZ",
  "tz1PB7jN9GnNppn8hEbrherUAt4n3bv3wgNn",
  "tz1cuj9Cgi19KMRi83UuypS89kXM435qdhmy",
  "tz1Kt4P8BCaP93AEV4eA7gmpRryWt5hznjCP",
  "tz1cX93Q3KsiTADpCC4f12TBvAmS5tw7CW19",
  "tz1NEKxGEHsFufk87CVZcrqWu8o22qh46GK6",
  "tz1W2hEsS1mj7dHPZ6267eeM4HDWJoG3s13n"
];
var mockImplicitAddress = (index) => {
  if (index >= validMockPkhs.length) {
    throw Error("index out of bound");
  }
  return { type: "implicit", pkh: validMockPkhs[index] };
};
var mockPk = (index) => `edpkuwYWCugiYG7nMnVUdopFmyc3sbMSiLqsJHTQgGtVhtSdLSw6H${index}`;

// src/utils/address.ts
var parseAddressMap = {
  [ChainNamespaces.TEZOS]: parsePkh,
  [ChainNamespaces.EVM]: parseEvmAddress,
  [ChainNamespaces.BITCOIN]: parseBitcoinAddress
};
var isAddressValidMap = {
  [ChainNamespaces.TEZOS]: isAddressValid,
  [ChainNamespaces.EVM]: isEvmAddressValid,
  [ChainNamespaces.BITCOIN]: isBitcoinAddressValid
};
var parseChainAddress = (address3, chainId) => {
  const { namespace } = parseChainFullId(chainId);
  return parseAddressMap[namespace](address3);
};
var parseChainAddressByNameSpace = (address3, namespace) => parseAddressMap[namespace](address3);
var isChainAddressValid = (address3, chainId) => {
  const { namespace } = parseChainFullId(chainId);
  if (namespace === ChainNamespaces.TEZOS && isSaplingAddress(address3)) {
    return true;
  }
  return isAddressValidMap[namespace](address3);
};
var getChainAddress = (account, chainId = ChainFullIds.TEZOS) => (
  // TODO: make chains required in the account object
  // chains property might be undefined as it's not yet implemented across the codebase
  account.chains?.[chainId]?.address ?? account.address
);
var getChainDecimals = (account, chainId = ChainFullIds.TEZOS) => account?.chains?.[chainId]?.decimals ?? TEZ_DECIMALS;
var detectChainNamespace = (address3) => {
  if (!address3 || address3.trim() === "") {
    return null;
  }
  const trimmedAddress = address3.trim();
  if (isSaplingAddress(trimmedAddress)) {
    return ChainNamespaces.TEZOS;
  }
  for (const [namespace, validator] of Object.entries(isAddressValidMap)) {
    if (validator(trimmedAddress)) {
      return namespace;
    }
  }
  return null;
};

// src/utils/transaction.ts
import { formatEther, parseEther } from "ethers";
var parseToSmallestUnitMap = {
  [ChainNamespaces.TEZOS]: tezToMutez,
  [ChainNamespaces.EVM]: parseEther,
  [ChainNamespaces.BITCOIN]: bitcoinToSatoshi
};
var parseToNormalUnitMap = {
  [ChainNamespaces.TEZOS]: mutezToTez,
  [ChainNamespaces.EVM]: formatEther,
  [ChainNamespaces.BITCOIN]: satoshiToBitcoin
};
var formatToNormalUnitMap = {
  [ChainNamespaces.TEZOS]: formatTezAmount,
  [ChainNamespaces.EVM]: formatEthersAmount,
  [ChainNamespaces.BITCOIN]: formatBitcoinAmount
};
var parseToSmallestUnit = (amount, chainId) => {
  const { namespace } = parseChainFullId(chainId);
  return parseToSmallestUnitMap[namespace](amount).toString();
};
var parseToNormalUnit = (amount, chainId) => {
  const { namespace } = parseChainFullId(chainId);
  return parseToNormalUnitMap[namespace](amount).toString();
};
var formatToNormalUnit = (amount, chainId, ...props) => {
  const { namespace } = parseChainFullId(chainId);
  return formatToNormalUnitMap[namespace](amount, ...props);
};

// src/utils/format.ts
var formatCurrency = (_balance, currency = "USD") => {
  const balance = typeof _balance === "number" && !Number.isNaN(_balance) ? _balance : 0;
  const needsExtraPrecision = balance > 0 && Math.trunc(balance * 100) === 0;
  const decimals = needsExtraPrecision ? 6 : 2;
  const factor = 10 ** decimals;
  const truncated = Math.trunc(balance * factor) / factor;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals
  });
  return formatter.format(truncated);
};
var formatBalance = (balance = "0") => {
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  });
  return formatter.format(Number(balance));
};

// src/utils/token.ts
import { BigNumber as BigNumber4 } from "bignumber.js";

// src/core/tezos/Account.ts
var DEFAULT_ACCOUNT_LABEL = "Account";

// src/core/tezos/AccountOperations.ts
import { CustomError as CustomError7 } from "@umami/utils";
import { BigNumber as BigNumber2 } from "bignumber.js";
var totalFee = (estimates) => estimates.map((estimate2) => estimate2.fee).reduce((acc, curr) => acc.plus(curr), BigNumber2(0)).toNumber();
var makeAccountOperations = (sender, signer, operations) => {
  switch (sender.type) {
    case "ledger":
    case "mnemonic":
    case "social":
    case "secret_key":
      if (sender.address.pkh !== signer.address.pkh) {
        throw new CustomError7("Sender and Signer must be the same");
      }
      return {
        type: "implicit",
        operations,
        signer: sender,
        sender
      };
    case "multisig":
      return {
        type: "proposal",
        operations,
        sender,
        signer
      };
  }
};

// src/core/tezos/constants/Hints.ts
var finalizationConsequences = "Finalizing unstaked funds makes them available on your spendable balance. They can now be spent or staken again to earn more rewards.";
var unstakingConsequences = `Unstaked tez immediately stop accruing staking rewards, but sill give rights to delegation rewards. Unstaked tez remain locked for up to 4 cycles (~11 days), after which they can be finalized on request. ${finalizationConsequences}`;
var Hints = {
  delegation: {
    header: "It takes at least 3 full cycles (~9 days) to start receiving delegation rewards",
    description: "The actual delay will depend on your chosen baker payout strategy. Bakers typically distribute delegation rewards every cycle (~3 days). Ensure the delegation fee offered by the baker is less than 100%; otherwise, you may not receive rewards. Delegation means that all your funds are delegated to one baker. The funds remain spendable, and you can cancel or change the delegation at any time."
  },
  undelegation: {
    header: "Stops both delegation and staking. Restoring rewards takes 2 cycles (~6 days).",
    description: `Undelegation takes effect immediately. However, you will still receive delegation rewards for the current and the next 2 cycles, as baking rights are computed 2 cycles in advance. By undelegating, you also initiate the unstaking process if you have staked. ${unstakingConsequences}`
  },
  stake: {
    header: "You will start receiving rewards immediately (within the current cycle)",
    description: `Your spendable balance will be reduced by the amount staked. You will start accruing staking rewards immediately (within the current cycle). Staking rewards are compounded on your staked balance. You can cancel staking any time. ${unstakingConsequences}`
  },
  unstake: {
    header: "Unstake request take effect immediately, but funds remain frozen for up to 4 cycles (~10 days)",
    description: unstakingConsequences
  },
  finalize_unstake: {
    header: "Takes 1 block (8 seconds) to complete.",
    description: `Finalizing applies to all finalizable funds in your balance. ${finalizationConsequences}`
  }
};

// src/core/tezos/constants/Titles.ts
var Titles = {
  tez: "Send request",
  contract_call: "Contract call request",
  delegation: "Delegation request",
  undelegation: "Undelegation request",
  contract_origination: "Origination request",
  stake: "Stake request",
  unstake: "Unstake request",
  finalize_unstake: "Finalize unstake Request",
  batch: "Batch request",
  //EVM
  personal_sign: "Sign in request",
  eth_signTransaction: "Sign transaction request",
  eth_approve: "Approval transaction request",
  eth_transfer: "Transaction request",
  eth_placeOrder: "Place order request",
  eth_sendTransaction: "Transaction request",
  eth_signTypedData: "Sign typed data request"
};

// src/core/tezos/decodeBeaconPayload.ts
import { CODEC, Uint8ArrayConsumer, getCodec } from "@taquito/local-forging";
import { DefaultProtocol, unpackData } from "@taquito/michel-codec";
import { bytesToString, hex2buf } from "@taquito/utils";
import { SigningType } from "@tezos-x/octez.connect-wallet";
import { CustomError as CustomError8 } from "@umami/utils";
var decodeBeaconPayload = (payload, signingType) => {
  try {
    if (!payload.length) {
      return { result: "" };
    }
    let result = payload;
    switch (signingType) {
      case SigningType.MICHELINE:
      case SigningType.OPERATION: {
        if (getSigningTypeFromPayload(payload) !== signingType) {
          throw new CustomError8("Invalid prefix for signing type");
        }
        try {
          const consumer = Uint8ArrayConsumer.fromHexString(payload);
          const uint8array = consumer.consume(consumer.length());
          const parsed = unpackData(uint8array);
          if ("string" in parsed && Object.keys(parsed).length === 1) {
            result = parsed.string;
          } else {
            result = JSON.stringify(parsed);
          }
        } catch {
          const prefixLen = signingType === SigningType.MICHELINE ? 12 : 2;
          result = bytesToString(payload.slice(prefixLen));
          if (result.length === 0) {
            throw new CustomError8("Invalid payload. Failed to decode.");
          }
        }
        break;
      }
      case SigningType.RAW: {
        try {
          result = JSON.stringify(parseOperationMicheline(payload));
        } catch {
          result = new TextDecoder("utf-8", { fatal: true }).decode(hex2buf(payload));
        }
        break;
      }
      default: {
        throw new CustomError8(`Unsupported signing type: ${signingType}`);
      }
    }
    if (!isValidASCII(result)) {
      throw new CustomError8("Invalid payload. Only ASCII characters are supported.");
    }
    return { result };
  } catch (error) {
    console.error(error);
    return { result: payload, error: "Cannot parse Beacon payload" };
  }
};
var isValidASCII = (str) => str.split("").every((char) => char.charCodeAt(0) < 128);
var parseOperationMicheline = (payload) => getCodec(CODEC.MANAGER, DefaultProtocol).decoder(payload);
var getSigningTypeFromPayload = (payload) => payload.startsWith("05") ? SigningType.MICHELINE : payload.startsWith("03") ? SigningType.OPERATION : SigningType.RAW;

// src/core/tezos/estimate.ts
import { CustomError as CustomError11 } from "@umami/utils";

// src/core/tezos/helpers.ts
import { OpKind } from "@taquito/rpc";
import { CustomError as CustomError10 } from "@umami/utils";
import { shuffle, zipWith } from "lodash";

// src/core/tezos/Operation.ts
import { MANAGER_LAMBDA } from "@taquito/taquito";
import { CustomError as CustomError9 } from "@umami/utils";
import { isEqual } from "lodash";
var SafeSendAssetType = /* @__PURE__ */ ((SafeSendAssetType2) => {
  SafeSendAssetType2["TEZ"] = "tez";
  SafeSendAssetType2["FA12"] = "fa1.2";
  SafeSendAssetType2["FA2"] = "fa2";
  return SafeSendAssetType2;
})(SafeSendAssetType || {});
var LAMBDA_HEADER = [
  { prim: "DROP" },
  { prim: "NIL", args: [{ prim: "operation" }] }
];
var makeMultisigApproveOrExecuteOperation = (contract, entrypoint, operationId) => makeContractCallOperation(contract, entrypoint, {
  int: operationId
});
var makeMultisigProposeOperation = (contract, proposedOperations) => {
  const lambdaActions = toBatchLambda(proposedOperations);
  return makeContractCallOperation(contract, "propose", lambdaActions);
};
var makeContractCallOperation = (contract, entrypoint, args, amount = "0") => ({
  type: "contract_call",
  contract,
  entrypoint,
  args,
  amount
});
var headlessLambda = (lambda) => {
  if (isEqual(lambda.slice(0, 2), LAMBDA_HEADER)) {
    return lambda.slice(2);
  }
  return lambda;
};
var toLambda = (operation) => {
  switch (operation.type) {
    case "tez":
      switch (operation.recipient.type) {
        case "implicit":
          return MANAGER_LAMBDA.transferImplicit(operation.recipient.pkh, Number(operation.amount));
        case "contract":
          return MANAGER_LAMBDA.transferToContract(
            operation.recipient.pkh,
            Number(operation.amount)
          );
        default:
          throw new CustomError9(`${operation.recipient.type} is not supported yet`);
      }
    // eslint-disable-next-line no-fallthrough
    case "fa1.2":
      return contractLambda(
        operation,
        FA12_TRANSFER_ARG_TYPES,
        makeFA12TransactionParameter(operation)
      );
    case "fa2": {
      return contractLambda(
        operation,
        FA2_TRANSFER_ARG_TYPES,
        makeFA2TransactionParameter(operation)
      );
    }
    case "delegation":
      return MANAGER_LAMBDA.setDelegate(operation.recipient.pkh);
    case "undelegation":
      return MANAGER_LAMBDA.removeDelegate();
    case "contract_origination":
    case "contract_call":
    case "stake":
    case "unstake":
    case "finalize_unstake":
    case "safe_send":
    case "claim_safe_send":
    case "withdraw_safe_send":
    case "approve":
    case "personal_sign":
    case "eth_approve":
    case "eth_transfer":
    case "eth_placeOrder":
    case "eth_sendTransaction":
    case "eth_sendRawTransaction":
    case "eth_signTypedData":
    case "eth_signTransaction":
      throw new CustomError9(`${operation.type} is not supported yet`);
  }
};
var toBatchLambda = (operations) => {
  const opsLambdas = operations.map(toLambda).flatMap(headlessLambda);
  return [...LAMBDA_HEADER, ...opsLambdas];
};
var FA2_TRANSFER_ARG_TYPES = {
  args: [
    {
      args: [
        {
          annots: ["%from_"],
          prim: "address"
        },
        {
          annots: ["%txs"],
          args: [
            {
              args: [
                {
                  annots: ["%to_"],
                  prim: "address"
                },
                {
                  args: [
                    {
                      annots: ["%token_id"],
                      prim: "nat"
                    },
                    {
                      annots: ["%amount"],
                      prim: "nat"
                    }
                  ],
                  prim: "pair"
                }
              ],
              prim: "pair"
            }
          ],
          prim: "list"
        }
      ],
      prim: "pair"
    }
  ],
  prim: "list"
};
var FA12_TRANSFER_ARG_TYPES = {
  args: [
    {
      annots: [":from"],
      prim: "address"
    },
    {
      args: [
        {
          annots: [":to"],
          prim: "address"
        },
        {
          annots: [":value"],
          prim: "nat"
        }
      ],
      prim: "pair"
    }
  ],
  prim: "pair"
};
var contractLambda = (operation, argTypes, transactionParameter) => [
  ...LAMBDA_HEADER,
  {
    prim: "PUSH",
    args: [
      { prim: "address" },
      { string: operation.contract.pkh + "%" + transactionParameter.entrypoint }
    ]
  },
  {
    prim: "CONTRACT",
    args: [argTypes]
  },
  // If contract is not valid then fail and rollback the whole transaction
  [{ prim: "IF_NONE", args: [[{ prim: "UNIT" }, { prim: "FAILWITH" }], []] }],
  { prim: "PUSH", args: [{ prim: "mutez" }, { int: "0" }] },
  { prim: "PUSH", args: [argTypes, transactionParameter.value] },
  { prim: "TRANSFER_TOKENS" },
  { prim: "CONS" }
];
var makeFA12TransactionParameter = ({
  sender,
  recipient,
  amount
}) => ({
  entrypoint: "transfer",
  value: {
    prim: "Pair",
    args: [
      {
        string: sender.pkh
      },
      {
        prim: "Pair",
        args: [
          {
            string: recipient.pkh
          },
          {
            int: amount
          }
        ]
      }
    ]
  }
});
var makeFA2TransactionParameter = ({
  sender,
  recipient,
  tokenId,
  amount
}) => ({
  entrypoint: "transfer",
  value: [
    {
      prim: "Pair",
      args: [
        {
          string: sender.pkh
        },
        [
          {
            prim: "Pair",
            args: [
              {
                string: recipient.pkh
              },
              {
                prim: "Pair",
                args: [
                  {
                    int: tokenId
                  },
                  {
                    int: amount
                  }
                ]
              }
            ]
          }
        ]
      ]
    }
  ]
});
var operationSign = (destination) => {
  switch (destination) {
    case "incoming":
      return "+";
    case "outgoing":
      return "-";
    case "unrelated":
      return "";
  }
};

// src/core/tezos/helpers.ts
var selectRandomElements = (arr, n) => shuffle(arr.map((value, index) => ({ value, index }))).slice(0, n).sort((a, b) => a.index - b.index);
var operationToTaquitoOperation = (operation) => {
  switch (operation.type) {
    case "tez":
      return {
        kind: OpKind.TRANSACTION,
        to: operation.recipient.pkh,
        amount: parseInt(operation.amount),
        mutez: true
      };
    case "contract_call":
      return {
        kind: OpKind.TRANSACTION,
        to: operation.contract.pkh,
        amount: parseInt(operation.amount),
        mutez: true,
        parameter: { entrypoint: operation.entrypoint, value: operation.args }
      };
    case "delegation":
      return {
        kind: OpKind.DELEGATION,
        source: operation.sender.pkh,
        delegate: operation.recipient.pkh
      };
    case "undelegation":
      return {
        kind: OpKind.DELEGATION,
        source: operation.sender.pkh,
        delegate: void 0
      };
    case "fa1.2":
      return {
        kind: OpKind.TRANSACTION,
        amount: 0,
        to: operation.contract.pkh,
        parameter: makeFA12TransactionParameter(operation)
      };
    case "fa2":
      return {
        kind: OpKind.TRANSACTION,
        amount: 0,
        to: operation.contract.pkh,
        parameter: makeFA2TransactionParameter(operation)
      };
    case "contract_origination": {
      if (isValidMichelson(operation.storage)) {
        return {
          kind: OpKind.ORIGINATION,
          code: operation.code,
          init: operation.storage
        };
      }
      return {
        kind: OpKind.ORIGINATION,
        code: operation.code,
        storage: operation.storage
      };
    }
    case "stake":
      return {
        kind: OpKind.TRANSACTION,
        amount: parseInt(operation.amount),
        source: operation.sender.pkh,
        to: operation.sender.pkh,
        parameter: { entrypoint: "stake", value: { prim: "Unit" } },
        mutez: true
      };
    case "unstake":
      return {
        kind: OpKind.TRANSACTION,
        amount: parseInt(operation.amount),
        source: operation.sender.pkh,
        to: operation.sender.pkh,
        parameter: { entrypoint: "unstake", value: { prim: "Unit" } },
        mutez: true
      };
    case "finalize_unstake":
      return {
        kind: OpKind.TRANSACTION,
        amount: 0,
        source: operation.sender.pkh,
        to: operation.sender.pkh,
        parameter: { entrypoint: "finalize_unstake", value: { prim: "Unit" } }
      };
    case "safe_send":
    case "claim_safe_send":
    case "withdraw_safe_send":
      throw new CustomError10(`${operation.type} is not supported yet`);
    case "personal_sign":
    case "eth_sendTransaction":
    case "eth_sendRawTransaction":
    case "eth_approve":
    case "eth_transfer":
    case "eth_placeOrder":
    case "eth_signTypedData":
    case "eth_signTransaction":
    case "approve":
      throw new CustomError10(
        `${operation.type} is not Tezos operations. It's an Ethereum operation.`
      );
  }
};
var operationsToBatchParams = ({
  type: operationsType,
  operations: originalOperations,
  sender
}) => {
  const operations = operationsType === "implicit" ? originalOperations : [makeMultisigProposeOperation(sender.address, originalOperations)];
  return operations.map(operationToTaquitoOperation);
};
var operationsToWalletParams = (operations) => zipWith(operationsToBatchParams(operations), operations.estimates, (operation, estimate2) => ({
  ...operation,
  ...estimate2
}));
var withTimeout = (fn, timeout, errorMessage) => Promise.race([
  fn(),
  // it's safe to use the same type parameter T here
  // because we're going to throw anyway which stops the execution
  new Promise(
    (_, reject) => setTimeout(() => {
      reject(new CustomError10(errorMessage || "The operation has timed out"));
    }, timeout)
  )
]);

// src/core/tezos/estimate.ts
var estimate = async (operations, network) => {
  const tezosToolkit = await makeToolkit({
    type: "fake",
    signer: operations.signer,
    network
  });
  try {
    const estimates = await tezosToolkit.estimate.batch(operationsToBatchParams(operations));
    let revealEstimate = void 0;
    if (estimates.length > operations.operations.length) {
      revealEstimate = estimateToEstimation(estimates.shift());
    }
    return {
      ...operations,
      estimates: estimates.map(estimateToEstimation),
      revealEstimate
    };
  } catch (err) {
    const isRevealed = await isAccountRevealed(operations.signer.address.pkh, network);
    if (!isRevealed) {
      throw new CustomError11(`Signer address is not revealed on the ${network.name}.`);
    }
    throw err;
  }
};
var estimateToEstimation = (estimate2) => ({
  storageLimit: estimate2.storageLimit,
  gasLimit: estimate2.gasLimit,
  // The way taquito works we need to take the max of suggestedFeeMutez and totalCost
  // because the suggestedFeeMutez does not include the storage & execution cost
  // and in these cases the totalCost is the one to go (so, for contract calls)
  // though totalCost doesn't work well with simple tez transfers and suggestedFeeMutez is more accurate
  fee: Math.max(estimate2.suggestedFeeMutez, estimate2.totalCost)
});

// src/core/tezos/execute.ts
var executeOperations = async (operations, tezosToolkit) => {
  const params = operationsToWalletParams(operations);
  return tezosToolkit.wallet.batch(params).send();
};

// src/core/tezos/Token.ts
import { BigNumber as BigNumber3 } from "bignumber.js";
import repeat from "lodash/repeat";

// src/indexers/tezos/tzkt/fetch.ts
import {
  blocksGet,
  contractsGetBigMapByNameKeys,
  delegatesGet,
  operationsGetByHash,
  operationsGetDelegations,
  operationsGetOriginations,
  operationsGetTransactions,
  tokensGetTokenBalances,
  tokensGetTokenTransfers
} from "@tzkt/sdk-api";
import * as tzktApi from "@tzkt/sdk-api";
import { CustomError as CustomError13 } from "@umami/utils";
import axios from "axios";
import { identity, pickBy, sortBy } from "lodash";

// src/indexers/tezos/tzkt/withRateLimit.ts
import Semaphore from "@chriscdn/promise-semaphore";
import { CustomError as CustomError12 } from "@umami/utils";
import promiseRetry from "promise-retry";
var tzktRateLimiter = new Semaphore(10);
var withRateLimit = (fn, options = { retry: true }) => tzktRateLimiter.acquire().then(() => {
  if (options.retry) {
    return promiseRetry((retry) => fn().catch(retry), { retries: 3, minTimeout: 100 });
  }
  return fn();
}).catch((error) => {
  if ("status" in error && "data" in error) {
    throw new CustomError12(
      `Fetching data from tzkt failed with: ${error.status}, ${error.data}`
    );
  }
  throw error;
}).finally(() => tzktRateLimiter.release());

// src/indexers/tezos/tzkt/fetch.ts
var SafeSendContracts;
var setSafeSendContracts = (contracts) => {
  SafeSendContracts = /* @__PURE__ */ new Map();
  contracts.forEach(({ chainId, assetType, mainnet, shadownet }) => {
    SafeSendContracts.set(`${chainId}-${assetType}`, { mainnet, shadownet });
  });
};
var getSafeSendContracts = () => SafeSendContracts;
var getAccounts = async (pkhs, network) => withRateLimit(
  () => fetch(
    `${network.tzktApiUrl}/v1/accounts?${new URLSearchParams({
      ["address.in"]: pkhs.join(","),
      ["select.fields"]: "address,balance,delegate,stakedBalance,unstakedBalance,rollupBonds,smartRollupBonds"
    })}`
  ).then((res) => res.json())
);
var getAllClaimTxForReceiver = async (contractAddress, network, receiver) => withRateLimit(
  async () => await axios.get(
    `${network.tzktApiUrl}/v1/contracts/${contractAddress}/bigmaps/sending/keys?value.receiver=${receiver}&active=true`
  )
);
var getRecvForSafeSendTx = async (contractAddress, network, hash) => withRateLimit(
  async () => axios.get(
    `${network.tzktApiUrl}/v1/contracts/${contractAddress}/bigmaps/sending/keys/${hash}?active=true`
  )
);
var getTokenBalances = async (address3, network) => withRateLimit(
  () => tokensGetTokenBalances(
    {
      account: { eq: address3 },
      balance: { gt: "0" },
      limit: 1e4
    },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getDelegations = async (address3, network, options) => withRateLimit(
  () => operationsGetDelegations(
    { sender: { eq: address3 }, ...options },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getTransactions = async (address3, network, options) => withRateLimit(
  () => operationsGetTransactions(
    {
      anyof: { fields: ["sender", "target", "initiator"], eq: address3 },
      ...options
    },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getOriginations = async (address3, network, options) => withRateLimit(
  () => operationsGetOriginations(
    { sender: { eq: address3 }, ...options },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getStakingOperations = async (address3, network, options) => withRateLimit(async () => {
  const params = pickBy(
    {
      limit: options.limit,
      "sender.eq": address3,
      "sort.asc": options.sort.asc,
      "sort.desc": options.sort.desc,
      "offset.cr": options.offset?.cr
      // TODO: add select.fields
    },
    identity
  );
  const response = await fetch(
    `${network.tzktApiUrl}/v1/operations/staking?${new URLSearchParams(params)}`
  );
  const data = await response.json();
  return data.map(
    (operation) => ({ ...operation, type: operation.action })
  );
});
var getCombinedOperations = async (address3, network, options) => {
  const limit = options?.limit || 100;
  const sort = options?.sort ?? "desc";
  const tzktRequestOptions = {
    limit,
    offset: options?.lastId ? { cr: options.lastId } : void 0,
    sort: { [sort]: "id" }
  };
  const operations = await Promise.all([
    getTransactions(address3, network, tzktRequestOptions),
    getDelegations(address3, network, tzktRequestOptions),
    getOriginations(address3, network, tzktRequestOptions),
    getTokenTransfers(address3, network, tzktRequestOptions),
    getStakingOperations(address3, network, tzktRequestOptions),
    getSafeSendTransactions(address3, network, tzktRequestOptions)
  ]);
  const allFetchedOperations = operations.flat();
  const uniqueOpsMap = /* @__PURE__ */ new Map();
  for (const op of allFetchedOperations) {
    const existingOp = uniqueOpsMap.get(op.id);
    if (!existingOp || op.type === "safe_send") {
      uniqueOpsMap.set(op.id, op);
    }
  }
  const deduplicatedOperations = Array.from(uniqueOpsMap.values());
  return sortBy(
    deduplicatedOperations,
    (operation) => sort === "asc" ? operation.id : -operation.id
  ).slice(0, limit);
};
var getRelatedTokenTransfers = async (transactionIds, network) => {
  if (transactionIds.length === 0) {
    return [];
  }
  return withRateLimit(
    () => tokensGetTokenTransfers(
      // tzkt doesn't work with the `in` operator correctly
      // the only way is to have just one element in it and join it with a comma manually
      { transactionId: { in: [transactionIds.join(",")] } },
      { baseUrl: network.tzktApiUrl }
    )
  );
};
var getTokenTransfers = async (address3, network, options) => withRateLimit(async () => {
  const tokenTransfers = await tokensGetTokenTransfers(
    {
      anyof: {
        fields: ["from", "to"],
        eq: address3
      },
      ...options
    },
    {
      baseUrl: network.tzktApiUrl
    }
  );
  return tokenTransfers.map((transfer) => ({
    ...transfer,
    type: "token_transfer"
  }));
});
var getTezosPriceInUSD = () => withRateLimit(() => tzktApi.quotesGetLast().then((quote) => quote.usd));
var getLatestBlock = async (network) => withRateLimit(async () => {
  const [block] = await blocksGet(
    {
      limit: 1,
      sort: { desc: "id" }
    },
    {
      baseUrl: network.tzktApiUrl
    }
  );
  return { level: block.level, cycle: block.cycle };
});
var getBakers = async (network) => withRateLimit(
  () => delegatesGet(
    {
      sort: { desc: "votingPower" },
      active: { eq: true },
      limit: 1e4,
      select: { fields: ["address,alias,votingPower"] }
    },
    {
      baseUrl: network.tzktApiUrl
    }
  ).then(
    (delegates) => delegates.map((delegate) => ({
      name: delegate.alias || "Unknown baker",
      address: delegate.address,
      stakingBalance: delegate.votingPower ?? 0
    }))
  )
);
var getPendingUnstakeRequests = async (network, address3) => withRateLimit(async () => {
  const requests = await fetch(
    `${network.tzktApiUrl}/v1/staking/unstake_requests?${new URLSearchParams({
      limit: "10000",
      "staker.eq": address3,
      type: "unstake",
      "status.ne": "finalized",
      "select.fields": "cycle,actualAmount,staker,status"
    })}`
  ).then((res) => res.json());
  return requests.map((request) => ({
    cycle: request.cycle,
    amount: request.actualAmount,
    staker: request.staker,
    status: request.status
  }));
});
var getOperationsByHash = (hash, network) => withRateLimit(() => operationsGetByHash(hash, {}, { baseUrl: network.tzktApiUrl }));
var convertToSafeSendAssetType = (map, address3) => {
  const tezKey = `${ChainIds.TEZOS}-${"tez" /* TEZ */}`;
  const fa12Key = `${ChainIds.TEZOS}-${"fa1.2" /* FA12 */}`;
  if (SafeSendContracts.get(tezKey)?.mainnet === address3 || SafeSendContracts.get(tezKey)?.shadownet === address3) {
    return "tez" /* TEZ */;
  } else if (SafeSendContracts.get(fa12Key)?.mainnet === address3 || SafeSendContracts.get(fa12Key)?.shadownet === address3) {
    return "fa1.2" /* FA12 */;
  } else {
    return "fa2" /* FA2 */;
  }
};
var getSafeSendContractForAsset = (assetType, network) => {
  const contracts = getSafeSendContracts();
  const key = `${ChainIds.TEZOS}-${assetType}`;
  const contractInfo = contracts.get(key);
  if (!contractInfo) {
    console.error(`SafeSend contract info not found for key: ${key}`);
    return void 0;
  }
  return network.name === "mainnet" ? contractInfo.mainnet : contractInfo.shadownet;
};
var convertBigMapKeyToClaimableSafeSendData = (item, role, assetType, contractAddress) => {
  const amount = item.value.amount;
  const FA12TokenContractAddress = item.value.token_contract ?? "";
  const FA2TokenList = item.value.batch ? item.value.batch : [];
  const comment = item.value.comment ?? "";
  const commonData = {
    ...item,
    assetType,
    contractAddress,
    sender: item.value.sender,
    receiver: item.value.receiver,
    comment,
    amount,
    FA12TokenContractAddress,
    FA2TokenList
  };
  return commonData;
};
var getSafeSendOpsForAddress = async (chainId, network, address3, options = { role: "both", onlyActive: true }) => {
  const assetTypesToFetch = ["tez" /* TEZ */, "fa1.2" /* FA12 */, "fa2" /* FA2 */];
  const allClaimableItems = [];
  const claimPromises = assetTypesToFetch.map(async (assetType) => {
    const contractAddress = getSafeSendContractForAsset(assetType, network);
    if (!contractAddress) {
      console.error(`No contract address found for ${assetType} on ${network.name}. Skipping.`);
      return [];
    }
    try {
      let claims = [];
      if (options.role === "sender" || options.role === "both") {
        const senderResponse = await contractsGetBigMapByNameKeys(
          contractAddress,
          "sending",
          {
            active: options.onlyActive ? true : void 0,
            value: { eq: { jsonValue: address3, jsonPath: "sender" } },
            limit: 1e3,
            sort: { desc: "id" }
          },
          { baseUrl: network.tzktApiUrl }
        );
        claims = senderResponse.map(
          (item) => convertBigMapKeyToClaimableSafeSendData(item, "sender", assetType, contractAddress)
        );
      }
      if (options.role === "receiver" || options.role === "both") {
        const receiverResponse = await contractsGetBigMapByNameKeys(
          contractAddress,
          "sending",
          {
            active: options.onlyActive ? true : void 0,
            value: { eq: { jsonValue: address3, jsonPath: "receiver" } },
            limit: 1e3,
            sort: { desc: "id" }
          },
          { baseUrl: network.tzktApiUrl }
        );
        claims = [
          ...claims,
          ...receiverResponse.map(
            (item) => convertBigMapKeyToClaimableSafeSendData(item, "receiver", assetType, contractAddress)
          )
        ];
      }
      if (claims.length === 0) {
        return [];
      }
      return claims;
    } catch (error) {
      throw new CustomError13(
        `Error fetching SafeSend operations for ${assetType} on ${network.name} for address ${address3}: ${error}`
      );
    }
  });
  const results = await Promise.allSettled(claimPromises);
  allClaimableItems.push(
    ...results.flatMap((result) => result.status === "fulfilled" ? result.value : []).sort((a, b) => (b.lastLevel || 0) - (a.lastLevel || 0))
  );
  return allClaimableItems;
};
var getAllClaims = (chainId, network, receiver, onlyActive) => getSafeSendOpsForAddress(chainId, network, receiver, { role: "receiver", onlyActive });
var getSafeSendTransactions = async (address3, network, options) => {
  const safeSendContracts = getSafeSendContracts();
  const contractAddresses = Array.from(safeSendContracts.values()).map((contract) => network.name === "mainnet" ? contract.mainnet : contract.shadownet).filter((value) => value.startsWith("KT"));
  if (contractAddresses.length === 0) {
    return [];
  }
  const commonTxQueryOptions = {
    target: { in: contractAddresses.join(",") },
    entrypoint: { in: ["send,lock,claim,withdraw"] },
    limit: options.limit,
    sort: options.sort,
    offset: options.offset
  };
  const txsBySenderPromise = withRateLimit(
    () => operationsGetTransactions(
      {
        ...commonTxQueryOptions,
        sender: { eq: address3 }
      },
      { baseUrl: network.tzktApiUrl }
    )
  );
  const txsByReceiverPromise = withRateLimit(
    () => operationsGetTransactions(
      {
        ...commonTxQueryOptions,
        parameter: { eq: { jsonValue: address3, jsonPath: "receiver" } }
      },
      { baseUrl: network.tzktApiUrl }
    )
  );
  const activeOpsForAddress = getSafeSendOpsForAddress(ChainIds.TEZOS, network, address3, {
    role: "both",
    onlyActive: true
  });
  const [txsBySender, txsByReceiver, activeOpsResults] = await Promise.all([
    txsBySenderPromise,
    txsByReceiverPromise,
    activeOpsForAddress
  ]);
  return mergeSafeSendOperations(address3, txsBySender, txsByReceiver, activeOpsResults);
};
var mergeSafeSendOperations = (address3, txsBySender, txsByReceiver, bigMapData) => {
  const combinedTransactionsMap = /* @__PURE__ */ new Map();
  [...txsByReceiver, ...txsBySender].forEach((tx) => {
    if (tx.id && !combinedTransactionsMap.has(tx.id)) {
      combinedTransactionsMap.set(tx.id, tx);
    }
  });
  const uniqueTransactions = Array.from(combinedTransactionsMap.values());
  const activeOpsKeySet = /* @__PURE__ */ new Set();
  bigMapData.forEach((item) => {
    if (item.key) {
      activeOpsKeySet.add(item.key);
    }
  });
  const safeSendHashToStatusMap = /* @__PURE__ */ new Map();
  uniqueTransactions.forEach((tx) => {
    if (tx.parameter?.entrypoint && tx.parameter.value) {
      const entrypoint = tx.parameter.entrypoint;
      const safeSendKey = tx.parameter.value?.hash_ || tx.parameter.value?.hash;
      if (safeSendKey) {
        if (entrypoint === "withdraw") {
          safeSendHashToStatusMap.set(safeSendKey, "withdrawn");
        } else if (entrypoint === "claim") {
          safeSendHashToStatusMap.set(safeSendKey, "claimed");
        }
      }
    }
  });
  const safeSendOperations = uniqueTransactions.map((tx) => {
    if (!tx.parameter || !tx.parameter.entrypoint || !tx.parameter.value || !tx.target) {
      return null;
    }
    const entrypoint = tx.parameter.entrypoint;
    const value = tx.parameter.value;
    const safeSendKey = value?.hash_ || value?.hash;
    if (entrypoint !== "send" && entrypoint !== "lock") {
      return null;
    }
    let determinedSafeSendStatus;
    if (safeSendKey && activeOpsKeySet.has(safeSendKey)) {
      determinedSafeSendStatus = "open";
    } else if (safeSendKey && safeSendHashToStatusMap.has(safeSendKey)) {
      determinedSafeSendStatus = safeSendHashToStatusMap.get(safeSendKey);
    } else {
      determinedSafeSendStatus = "withdrawn";
    }
    let determinedAction;
    if (tx.sender.address === address3) {
      determinedAction = "send";
    } else if (value?.receiver && value.receiver === address3) {
      determinedAction = "claim";
    }
    if (!determinedAction) {
      console.warn(
        `Could not determine action for transaction ${tx.id} for the given address. Skipping.`
      );
      return null;
    }
    const determinedAssetType = convertToSafeSendAssetType(
      getSafeSendContracts(),
      tx.target.address
    );
    const operation = {
      id: tx.id,
      level: tx.level,
      timestamp: tx.timestamp,
      hash: tx.hash,
      counter: tx.counter,
      sender: tx.sender,
      bakerFee: tx.bakerFee,
      storageFee: tx.storageFee,
      allocationFee: tx.allocationFee,
      status: tx.status,
      type: "safe_send",
      action: determinedAction,
      assetType: determinedAssetType,
      safeSendStatus: determinedSafeSendStatus,
      target: tx.target,
      amount: tx.amount,
      parameter: { entrypoint, value }
    };
    return operation;
  }).filter((op) => op !== null);
  return safeSendOperations;
};

// src/indexers/tezos/tzkt/schemas.ts
import { z } from "zod";
var AddressSchema = z.object({ address: z.string(), alias: z.string().optional() });
var FA12TokenSchema = z.object({
  standard: z.string().regex(/^fa1\.2$/i),
  contract: AddressSchema
});
var FA2TokenSchema = z.object({
  standard: z.string().regex(/^fa2$/i),
  tokenId: z.string(),
  contract: AddressSchema
});
var NFTSchema = z.object({
  id: z.number(),
  standard: z.string().regex(/^fa2$/i),
  tokenId: z.string(),
  contract: AddressSchema,
  totalSupply: z.string().optional(),
  metadata: z.object({
    displayUri: z.string(),
    decimals: z.void().or(z.string().regex(/^0$/))
  })
});

// src/indexers/tezos/tzkt/testUtils.ts
var mockTzktTezTransfer = (sender, recipient, amount, timestamp = "2020-05-24T13:12:18Z", hash = "onqKcUZLbN9LdoJsPeRtDrHqYibm4osdtQJmGGQ4GAg3SjQUMT3", level = 10) => ({
  id: 12345,
  type: "transaction",
  sender: { address: sender },
  target: { address: recipient },
  timestamp,
  amount,
  hash,
  level,
  transactionId: 54321
});

// src/indexers/coinmarketcap/fetch.ts
import { CustomError as CustomError14 } from "@umami/utils";
var genericFetch = async (url, errorMsg) => withRateLimit(
  async () => {
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new CustomError14(`${errorMsg}: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  },
  { retry: false }
);
var fetchPrices = (tokenSymbols) => {
  const url = new URL(`${PRICING_API_URL}/cmc/v2/cryptocurrency/quotes/latest`);
  url.searchParams.set("symbol", tokenSymbols.join(","));
  return genericFetch(url, "Failed to fetch token prices");
};

// src/core/tezos/Token.ts
var fromRawToken = (rawToken) => {
  const metadata = rawToken.metadata;
  if (rawToken.standard === "fa1.2") {
    const fa1result = FA12TokenSchema.safeParse(rawToken);
    if (fa1result.success) {
      const contract = fa1result.data.contract.address;
      return {
        type: "fa1.2",
        chainId: ChainFullIds.TEZOS,
        metadata,
        contract,
        contractAlias: fa1result.data.contract.alias,
        tokenId: "0",
        id: `${contract}:0`
      };
    }
    console.warn("Invalid FA1 token: " + JSON.stringify(rawToken));
    return null;
  }
  const nftResult = NFTSchema.safeParse(rawToken);
  if (nftResult.success) {
    const contract = nftResult.data.contract.address;
    const tokenId = nftResult.data.tokenId;
    return {
      // if the nft has been parsed successfully then the metadata is definitely present
      metadata,
      type: "nft",
      id: `${contract}:${tokenId}`,
      contract,
      contractAlias: nftResult.data.contract.alias,
      tokenId,
      displayUri: nftResult.data.metadata.displayUri,
      totalSupply: nftResult.data.totalSupply,
      chainId: ChainFullIds.TEZOS
    };
  }
  const fa2result = FA2TokenSchema.safeParse(rawToken);
  if (fa2result.success) {
    const contract = fa2result.data.contract.address;
    const tokenId = fa2result.data.tokenId;
    return {
      type: "fa2",
      metadata,
      contract,
      contractAlias: fa2result.data.contract.alias,
      tokenId,
      id: `${contract}:${tokenId}`,
      chainId: ChainFullIds.TEZOS
    };
  }
  console.warn("Invalid FA2 token: " + JSON.stringify(rawToken));
  return null;
};
var fullId = (token) => `${token.contract}:${token.tokenId}`;
var defaultTokenName = (asset) => {
  switch (asset.type) {
    case "fa1.2":
      return DEFAULT_FA1_NAME;
    case "fa2":
      return DEFAULT_FA2_NAME;
    case "nft":
      return DEFAULT_NFT_NAME;
  }
};
var tokenNameSafe = (token) => tokenName(token) || defaultTokenName(token);
var tokenName = (token) => token.metadata?.name;
var defaultTokenSymbol = (token) => {
  switch (token.type) {
    case "fa1.2":
      return DEFAULT_FA1_SYMBOL;
    case "fa2":
      return DEFAULT_FA2_SYMBOL;
    case "nft":
      return DEFAULT_NFT_SYMBOL;
  }
};
var tokenSymbolSafe = (token) => tokenSymbol(token) || defaultTokenSymbol(token);
var tokenSymbol = (token) => token.metadata?.symbol;
var tokenDecimals = (asset) => asset.metadata?.decimals ?? DEFAULT_TOKEN_DECIMALS;
var getRealAmount = (token, prettyAmount, decimals) => {
  const amount = new BigNumber3(prettyAmount);
  const _decimals = decimals ?? tokenDecimals(token);
  return amount.multipliedBy(new BigNumber3(10).exponentiatedBy(_decimals)).toFixed();
};
var formatTokenAmount = (amount, decimals = DEFAULT_TOKEN_DECIMALS) => {
  const realAmount = BigNumber3(amount).dividedBy(BigNumber3(10).pow(decimals));
  try {
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: Number(decimals),
      maximumFractionDigits: Number(decimals)
    });
    return formatter.format(realAmount.toNumber());
  } catch (_) {
    console.warn(`Can't format token amount with decimals = ${decimals}`);
    return formatTokenAmount(amount, "0");
  }
};
var tokenPrettyAmount = (amount, token, options) => {
  if (token.type === "nft") {
    return amount;
  }
  const symbol = tokenSymbolSafe(token);
  const decimals = token.metadata?.decimals;
  const trailingSymbol = options?.showSymbol ? ` ${symbol}` : "";
  const result = formatTokenAmount(amount, decimals);
  return `${result}${trailingSymbol}`;
};
var artifactUri = (nft) => nft.metadata.artifactUri || nft.displayUri;
var thumbnailUri = (nft) => nft.metadata.thumbnailUri || nft.displayUri;
var mimeType = (nft) => nft.metadata.formats?.find((format3) => format3.uri === artifactUri(nft))?.mimeType;
var royalties = (nft) => {
  const royalties2 = nft.metadata.royalties;
  if (!royalties2) {
    return [];
  }
  const totalShares = Math.pow(10, Number(royalties2.decimals));
  const shares = Object.entries(royalties2.shares).map(([address3, share]) => ({
    address: address3,
    share: Number(share) * 100 / totalShares
  }));
  shares.sort((a, b) => a.share < b.share ? 1 : -1);
  return shares;
};
var metadataUri = (token, network) => `${tokenUri(token, network)}/metadata`;
var tokenUri = ({ contract, tokenId }, network) => `${network.explorerUrl}/${contract}/tokens/${tokenId}`;
var getSmallestUnit = (decimals) => {
  if (decimals < 0) {
    console.warn("Decimals cannot be negative");
    decimals = 0;
  }
  const leadingZeroes = decimals === 0 ? "" : "0." + repeat("0", decimals - 1);
  return `${leadingZeroes}1`;
};
var tokenContractName = (token) => token.contractAlias || token.contract;
var DEFAULT_FA1_NAME = "FA1.2 token";
var DEFAULT_FA2_NAME = "FA2 token";
var DEFAULT_NFT_NAME = "NFT";
var DEFAULT_FA1_SYMBOL = "FA1.2";
var DEFAULT_FA2_SYMBOL = "FA2";
var DEFAULT_NFT_SYMBOL = "NFT";
var DEFAULT_TOKEN_DECIMALS = "0";

// src/core/tezos/TokenBalance.ts
import { orderBy } from "lodash";
var fromRawTokenBalance = (raw) => {
  const token = fromRawToken(raw.token);
  if (!token || !raw.balance) {
    return null;
  }
  return { balance: raw.balance, lastLevel: raw.lastLevel, ...token };
};
var sortedByLastUpdate = (nfts) => orderBy(nfts, ["lastLevel", "id", "owner"], ["desc"]);

// src/core/tezos/beaconUtils.ts
import { TezosOperationType } from "@tezos-x/octez.connect-wallet";
import { WalletConnectError, WcErrorCode } from "@umami/utils";
var toAccountOperations = (operationDetails, signer) => {
  if (operationDetails.length === 0) {
    throw new WalletConnectError("Empty operation details!", WcErrorCode.INVALID_PARAMS, null);
  }
  const operations = operationDetails.map(
    (operation) => partialOperationToOperation(operation, signer)
  );
  return {
    type: "implicit",
    sender: signer,
    operations,
    signer
  };
};
var partialOperationToOperation = (partialOperation, signer) => {
  switch (partialOperation.kind) {
    case TezosOperationType.TRANSACTION: {
      const { destination, amount, parameters } = partialOperation;
      if (parameters) {
        if (isValidImplicitPkh(destination)) {
          switch (parameters.entrypoint) {
            case "stake":
              return { type: "stake", amount, sender: parseImplicitPkh(destination) };
            case "unstake":
              return { type: "unstake", amount, sender: parseImplicitPkh(destination) };
            case "finalize_unstake":
              return { type: "finalize_unstake", sender: parseImplicitPkh(destination) };
          }
        }
        return {
          type: "contract_call",
          amount,
          contract: parsePkh(destination),
          entrypoint: parameters.entrypoint,
          args: parameters.value
        };
      }
      return {
        type: "tez",
        amount,
        recipient: parseImplicitPkh(partialOperation.destination)
      };
    }
    case TezosOperationType.DELEGATION: {
      const { delegate } = partialOperation;
      if (delegate) {
        return {
          type: "delegation",
          sender: signer.address,
          recipient: parseImplicitPkh(delegate)
        };
      }
      return { type: "undelegation", sender: signer.address };
    }
    case TezosOperationType.ORIGINATION: {
      const { script } = partialOperation;
      const { code, storage } = script;
      return {
        type: "contract_origination",
        sender: signer.address,
        code,
        storage
      };
    }
    default:
      throw new WalletConnectError(
        `Unsupported operation kind: ${partialOperation.kind}`,
        WcErrorCode.METHOD_UNSUPPORTED,
        null
      );
  }
};

// src/core/tezos/testUtils.tsx
import { uUSD } from "@umami/test-utils";
var mockDelegation = (id, initialAmount, delegateAddress, delegateAlias, date = /* @__PURE__ */ new Date()) => ({
  type: "delegation",
  id,
  level: 3278793,
  timestamp: date.toISOString(),
  block: "BMGrLsKz89GdctsFamoGErgKfBjt2P9aoroCQFmzqbDBbwwfKQZ",
  hash: "oo6eXUdtvpRsFDsNR9YE7zngbeCU89FsZfxQHNzMmiaNJHniF67",
  counter: 13186782,
  sender: { address: mockImplicitAddress(id).pkh },
  gasLimit: 1100,
  gasUsed: 1e3,
  storageLimit: 0,
  bakerFee: 396,
  amount: initialAmount,
  prevDelegate: {
    alias: "Pool of Stake",
    address: "tz1gjwq9ybKEmqQrTmzCoVB3HESYV1Ekc5up"
  },
  newDelegate: {
    alias: delegateAlias,
    address: delegateAddress
  },
  status: "applied"
});
var mockTokenTransaction = (id) => ({
  from: { address: `mockSender${id}` },
  to: { address: `mockTarget${id}` },
  transactionId: 100 + id
});
var mockAccountLabel = (accountIndex) => accountIndex === 0 ? "Account" : `Account ${accountIndex + 1}`;
var mockImplicitAccount = (index, type = "mnemonic", fingerPrint = "mockPrint", label) => {
  switch (type) {
    case "mnemonic":
      return mockMnemonicAccount(index, { label, seedFingerPrint: fingerPrint });
    case "social":
      return mockSocialAccount(index, label);
    case "ledger":
      return mockLedgerAccount(index, label);
    case "secret_key":
      return mockSecretKeyAccount(index, label);
  }
};
var mockMnemonicAccount = (index, options) => ({
  id: "mockId",
  curve: "ed25519",
  derivationPath: getDefaultDerivationPath(index),
  derivationPaths: {
    [ChainFullIds.TEZOS]: getDefaultDerivationPath(index)
  },
  derivationPathTemplate: "44'/1729'/?'/0'",
  type: "mnemonic",
  address: mockImplicitAddress(index),
  pk: mockPk(index),
  seedFingerPrint: "mockPrint",
  isVerified: true,
  chains: {
    [ChainFullIds.TEZOS]: {
      address: mockImplicitAddress(index),
      derivationPath: getDefaultDerivationPath(index),
      derivationPathTemplate: defaultDerivationPathTemplate,
      curve: "ed25519",
      pk: mockPk(index),
      decimals: TEZ_DECIMALS
    },
    [ChainFullIds.ETHERLINK]: {
      address: mockImplicitAddress(index),
      pk: mockPk(index),
      derivationPath: void 0,
      derivationPathTemplate: void 0,
      curve: void 0,
      decimals: TEZ_DECIMALS
    }
  },
  ...options,
  label: options?.label ?? mockAccountLabel(index)
});
var mockSecretKeyAccount = (index, label) => ({
  type: "secret_key",
  curve: "ed25519",
  label: label || mockAccountLabel(index),
  address: mockImplicitAddress(index),
  pk: mockPk(index),
  id: "mockId"
});
var mockSocialAccount = (index, label, idp) => ({
  type: "social",
  label: label || mockAccountLabel(index),
  address: mockImplicitAddress(index),
  pk: mockPk(index),
  idp: idp || "google",
  curve: "secp256k1",
  id: "mockId"
});
var mockLedgerAccount = (index, label) => {
  const account = {
    type: "ledger",
    derivationPathTemplate: defaultDerivationPathTemplate,
    derivationPath: getDefaultDerivationPath(index),
    curve: "ed25519",
    label: label || mockAccountLabel(index),
    address: mockImplicitAddress(index),
    pk: mockPk(index),
    id: "mockId"
  };
  return account;
};
var mockMultisigAccount = (index, signers) => ({
  type: "multisig",
  address: mockContractAddress(index),
  label: `Multisig Account ${index}`,
  threshold: 1,
  signers: signers || [mockImplicitAddress(index)],
  pendingOperationsBigmapId: index,
  id: "mockId"
});
var mockNFTToken = (index, ownerPkh, options) => ({
  id: index,
  balance: "1",
  account: {
    address: ownerPkh
  },
  token: {
    id: index,
    contract: {
      address: mockContractAddress(index).pkh
    },
    tokenId: String(index),
    standard: "fa2",
    totalSupply: "1",
    metadata: {
      name: "Tezzardz #10",
      creators: ["George Goodwin (@omgidrawedit)"],
      decimals: "0",
      royalties: {
        shares: {
          tz1LLPWMyZ7gKsp3WnLfemyAYW6CoZoozku5: "5"
        },
        decimals: "2"
      },
      description: "Tezzardz is a collection of 4,200 programmatically, randomly generated, snazzy little fukrs on the Tezos blockchain.",
      displayUri: `ipfs://zb2rhXWQ9X95yxQwusNjftDSWVQYbGjFFFFBjJKQZ8uCrNcvV${index}`
    }
  },
  ...options
});
var mockFA2Token = (index, account, balance = 1, decimals = 4, symbol = "KL2", name = "Klondike2") => fromRawTokenBalance(
  mockFA2TokenRaw(index, account.address.pkh, { balance, decimals, symbol, name })
);
var mockFA12Token = (index, account, balance = 1) => fromRawTokenBalance(mockFA1TokenRaw(index, account.address.pkh, balance));
var mockFA2TokenRaw = (index, owner, tokenParams) => {
  const { balance, decimals, symbol, name, lastLevel } = {
    balance: 1,
    decimals: 4,
    symbol: "KL2",
    name: "Klondike2",
    lastLevel: 2247548,
    ...tokenParams
  };
  return {
    id: 10898270846977,
    account: {
      address: owner
    },
    token: {
      id: 10898194300929,
      contract: {
        address: mockContractAddress(index).pkh
      },
      tokenId: String(index),
      standard: "fa2",
      totalSupply: "13000000000",
      metadata: {
        name,
        symbol,
        decimals: String(decimals)
      }
    },
    balance: String(balance),
    transfersCount: 27,
    firstLevel: 288245,
    firstTime: "2022-03-24T15:36:50Z",
    lastLevel,
    lastTime: "2023-03-31T11:19:01Z"
  };
};
var mockFA1TokenRaw = (index, owner, balance = 1) => ({
  id: 10897662672897,
  account: {
    address: owner
  },
  token: {
    id: 10897625972737,
    contract: {
      address: mockContractAddress(index).pkh
    },
    tokenId: "0",
    standard: "fa1.2",
    totalSupply: "13000000"
  },
  balance: String(balance),
  transfersCount: 28,
  firstLevel: 288229,
  firstTime: "2022-03-24T15:32:10Z",
  lastLevel: 2215201,
  lastTime: "2023-03-27T13:30:37Z"
});
var mockNFTRaw = (index, owner, args) => {
  const tokenBalance = mockFA2TokenRaw(index, owner, { ...args, decimals: 0 });
  tokenBalance.token.metadata.displayUri = mockDisplayURI(index);
  return tokenBalance;
};
var mockDisplayURI = (index) => `ipfs://zdj7Wk92xWxpzGqT6sE4cx7umUyWaX2Ck8MrSEmPAR31sNWG${index}`;
var mockNFTBalance = (index, options) => {
  const displayUri = mockDisplayURI(index);
  return {
    id: `${mockContractAddress(index).pkh}:mockId${index}`,
    type: "nft",
    balance: "1",
    displayUri,
    chainId: ChainFullIds.TEZOS,
    contract: mockContractAddress(index).pkh,
    tokenId: "mockId" + index,
    totalSupply: "1",
    metadata: {
      displayUri,
      name: "Tezzardz #" + index,
      symbol: "FKR" + index
    },
    ...options
  };
};
var mockBaker = (index) => ({
  name: `label${index}`,
  address: mockImplicitAddress(index).pkh,
  stakingBalance: 1e5 + index
});
var mockTezOperation = (index) => ({
  type: "tez",
  amount: String(index),
  recipient: mockImplicitAddress(index + 1)
});
var mockFA12Operation = (index) => ({
  type: "fa1.2",
  amount: String(index),
  sender: mockImplicitAddress(index),
  recipient: mockImplicitAddress(index + 1),
  contract: mockContractAddress(index),
  tokenId: "0"
});
var mockNftOperation = (index) => ({
  type: "fa2",
  amount: String(index),
  sender: mockImplicitAddress(index),
  recipient: mockImplicitAddress(index + 1),
  contract: mockContractAddress(index),
  tokenId: String(index)
});
var mockFA2Operation = mockNftOperation;
var mockDelegationOperation = (index) => ({
  type: "delegation",
  sender: mockImplicitAddress(index),
  recipient: mockImplicitAddress(index + 1)
});
var mockUndelegationOperation = (index) => ({
  type: "undelegation",
  sender: mockImplicitAddress(index)
});
var mockContractOrigination = (index, storage = {}, code = []) => ({
  type: "contract_origination",
  storage,
  code,
  sender: mockImplicitAddress(index)
});
var mockContractCall = (index, code = []) => ({
  type: "contract_call",
  entrypoint: "test-entrypoint",
  amount: String(index),
  contract: mockContractAddress(index),
  args: code
});
var mockStakeOperation = (index) => ({
  type: "stake",
  amount: String(index),
  sender: mockImplicitAddress(index)
});
var mockUnstakeOperation = (index) => ({
  type: "unstake",
  amount: String(index),
  sender: mockImplicitAddress(index)
});
var mockFinalizeUnstakeOperation = (index) => ({
  type: "finalize_unstake",
  sender: mockImplicitAddress(index)
});
var mockContractContact = (index, network, label) => ({
  name: label || `Contact ${index}`,
  pkh: mockContractAddress(index).pkh,
  network,
  chainNamespace: "tezos"
});
var rawAccountFixture = (props) => ({
  address: mockImplicitAddress(0).pkh,
  balance: 1e6,
  delegate: { alias: "mega_baker", address: mockImplicitAddress(1).pkh },
  stakedBalance: 0,
  unstakedBalance: 0,
  rollupBonds: 0,
  smartRollupBonds: 0,
  ...props
});
var transactionFixture = (props) => ({
  amount: 1,
  target: { address: mockImplicitAccount(0).address.pkh },
  sender: { address: mockImplicitAccount(1).address.pkh },
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  timestamp: "2021-01-02T00:00:00.000Z",
  type: "transaction",
  status: "applied",
  ...props
});
var contractCallFixture = (props) => transactionFixture({
  target: { address: mockContractAddress(0).pkh },
  parameter: { entrypoint: "test-entrypoint" },
  ...props
});
var originationFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "origination",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  originatedContract: {
    address: mockContractAddress(0).pkh,
    typeHash: 1963879877,
    codeHash: -1890025422
  },
  status: "applied",
  ...props
});
var delegationFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "delegation",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  newDelegate: {
    address: mockImplicitAddress(1).pkh
  },
  amount: 5,
  status: "applied",
  ...props
});
var tokenTransferFixture = (props) => ({
  type: "token_transfer",
  amount: "500",
  transactionId: 56789,
  id: 278346,
  level: 10,
  from: { address: mockImplicitAccount(0).address.pkh },
  to: { address: mockImplicitAccount(1).address.pkh },
  token: uUSD(mockLedgerAccount(0).address).token,
  ...props
});
var stakeFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "stake",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  baker: {
    address: mockImplicitAddress(1).pkh
  },
  amount: 5,
  status: "applied",
  ...props
});
var unstakeFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "unstake",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  baker: {
    address: mockImplicitAddress(1).pkh
  },
  amount: 10,
  status: "applied",
  ...props
});
var finalizeUnstakeFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "finalize",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  amount: 15,
  status: "applied",
  ...props
});

// src/core/tezos/getAccountGroupLabel.ts
var getAccountGroupLabel = (account) => {
  switch (account.type) {
    case "mnemonic":
      return `Seedphrase ${account.seedFingerPrint}`;
    case "social":
      return "Social accounts";
    case "ledger":
      return "Ledger accounts";
    case "secret_key":
      return "Secret key accounts";
    case "multisig":
      return "Multisig accounts";
  }
};

// src/utils/token.ts
var DEFAULT_TOKEN_DECIMALS2 = 6;
var getTokenId = (token) => token.id;
var getNftId = (nft) => isTezosNft(nft) ? fullId(nft) : nft.id;
var parseTokenContractAddress = (token) => {
  if (isTezosToken(token)) {
    return parseContractPkh(token.contract);
  }
  return { type: "contract", pkh: token.address };
};
var parseNftContractAddress = (nft) => {
  if (isTezosNft(nft)) {
    return parseContractPkh(nft.contract);
  }
  return { type: "contract", pkh: nft.address };
};
var getTokenContractName = (nft) => isTezosNft(nft) ? tokenContractName(nft) : nft.address;
var getTokenContract = (nft) => {
  const targetNft = Array.isArray(nft) ? nft[0] : nft;
  return isTezosNft(targetNft) ? targetNft.contract : targetNft.address;
};
var isMultichainToken = (token) => "address" in token;
var isTezosToken = (token) => "contract" in token;
var isTezosNft = (token) => "contract" in token;
var formatTokenBalance = (balance, decimals = DEFAULT_TOKEN_DECIMALS2) => balance.toFixed(decimals);
var getTokenDecimals = (token) => {
  if (isTezosToken(token)) {
    return token.metadata?.decimals ?? 0;
  }
  return token.decimals;
};
var getRawAmount = (token, prettyAmount, decimals) => {
  const amount = new BigNumber4(prettyAmount);
  const _decimals = decimals ?? getTokenDecimals(token);
  return amount.multipliedBy(new BigNumber4(10).exponentiatedBy(_decimals)).toFixed();
};
var sanitizeTokenName = (name) => name?.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
var getTokenName = (token) => "name" in token ? token.name : token.metadata?.name;
var getTokenSymbol = (token) => "symbol" in token ? token.symbol : token.metadata?.symbol;
var getTokenPrice = (token, prices) => {
  const symbol = getTokenSymbol(token);
  if (!symbol) {
    return 0;
  }
  const priceList = symbol in prices ? prices[symbol] : void 0;
  if (!priceList || !priceList.length) {
    return 0;
  }
  let usdPrice = priceList[0].quote.USD.price ?? 0;
  if (priceList.length > 1) {
    const tokenName2 = sanitizeTokenName(getTokenName(token));
    if (tokenName2) {
      const exactMatch = priceList.find(
        (price) => sanitizeTokenName(price.slug) === tokenName2 || sanitizeTokenName(price.name) === tokenName2
      );
      usdPrice = exactMatch?.quote.USD.price ?? 0;
    }
  }
  return usdPrice;
};

// src/providers/bitcoin/testUtils.ts
var bitcoinAddressMainnet = "bc1qpdtryx3uvegvu5evhm8uyh9tpcw6l5kqj4gzjk";
var bitcoinAddressTestnet = "tb1qpdtryx3uvegvu5evhm8uyh9tpcw6l5kqj4gzjk";
var mockBitcoinCompatibleImplicitAddress = (coinType) => coinType === 0 ? bitcoinAddressMainnet : bitcoinAddressTestnet;

// src/providers/evm/testUtils.ts
var mockEvmCompatibleImplicitAddress = () => {
  let randomHex = "";
  for (let i = 0; i < 40; i++) {
    randomHex += Math.floor(Math.random() * 16).toString(16);
  }
  return {
    type: "implicit",
    pkh: `0x${randomHex}`
  };
};

// src/providers/fetch.ts
import { CustomError as CustomError15 } from "@umami/utils";
var genericFetch2 = async (url, errorMsg, options) => withRateLimit(async () => {
  const response = await fetch(url.toString(), options);
  if (!response.ok) {
    throw new CustomError15(`${errorMsg}: ${response.status} ${response.statusText}`);
  }
  return await response.json();
});

// src/providers/evm/fetch.ts
var fetchTxHistory = (url) => genericFetch2(url, "Failed to fetch transaction history");
var fetchTokens = (url) => genericFetch2(url, "Failed to fetch tokens");
var fetchNfts = (url) => genericFetch2(url, "Failed to fetch NFT");
var fetchTokensTransfers = (url) => genericFetch2(url, "Failed to fetch tokens transfers");

// src/providers/evm/LedgerSigner.ts
import _Eth from "@ledgerhq/hw-app-eth";
import {
  AbstractSigner,
  Signature,
  Transaction,
  TypedDataEncoder,
  assertArgument,
  copyRequest,
  getAccountPath,
  getAddress,
  hexlify,
  resolveAddress,
  resolveProperties,
  toUtf8Bytes
} from "ethers";
var Eth = "default" in _Eth ? _Eth.default : _Eth;
var LedgerSigner2 = class _LedgerSigner extends AbstractSigner {
  // A Promise that resolves to a created transport
  #transport;
  // The HD path
  #path;
  type = "ledger";
  /**
   *  Create a new **LedgerSigner** connected to the device over the
   *  %%transport%% and optionally connected to the blockchain via
   *  %%provider%%. The %%path%% follows the same logic as
   *  [[LedgerSigner_getPath]], defaulting to the default HD path of
   *  ``m/44'/60'/0'/0/0``.
   */
  constructor(transport, provider, path) {
    assertArgument(
      transport && (typeof transport === "object" || typeof transport === "function"),
      "invalid transport",
      "transport",
      transport
    );
    super(provider);
    if ("default" in transport) {
      transport = transport.default;
    }
    if (typeof transport.create === "function") {
      transport = transport.create();
    }
    this.#transport = Promise.resolve(transport);
    this.#path = _LedgerSigner.getPath(path);
  }
  /**
   *  The HD path for this account
   */
  get path() {
    return this.#path;
  }
  connect(provider) {
    return new _LedgerSigner(this.#transport, provider);
  }
  /**
   *  Returns a new LedgerSigner connected via the same transport
   *  and provider, but using the account at the HD %%path%%.
   */
  getSigner(path) {
    return new _LedgerSigner(this.#transport, this.provider, path);
  }
  /**
   *  Closes the transport connection to the Ledger device
   */
  async close() {
    const transport = await this.#transport;
    await transport.close();
  }
  async getPublicKeyPair() {
    try {
      const transport = await this.#transport;
      const obj = await new Eth(transport).getAddress(this.#path);
      return {
        publicKey: obj.publicKey,
        address: getAddress(obj.address)
      };
    } catch (error) {
      if (error.statusCode === 27404) {
        const e = new Error("device is not running Ethereum App");
        e.ledgerError = error;
        throw e;
      }
      throw error;
    }
  }
  async getAddress() {
    try {
      const transport = await this.#transport;
      const obj = await new Eth(transport).getAddress(this.#path);
      return getAddress(obj.address);
    } catch (error) {
      if (error.statusCode === 27404) {
        const e = new Error("device is not running Ethereum App");
        e.ledgerError = error;
        throw e;
      }
      throw error;
    }
  }
  async signTransaction(_tx) {
    try {
      _tx = copyRequest(_tx);
      const { to, from } = await resolveProperties({
        to: _tx.to ? resolveAddress(_tx.to, this.provider) : void 0,
        from: _tx.from ? resolveAddress(_tx.from, this.provider) : void 0
      });
      if (to != null) {
        _tx.to = to;
      }
      if (from != null) {
        _tx.from = from;
      }
      const tx = Transaction.from(_tx);
      const rawTx = tx.unsignedSerialized.substring(2);
      const resolution = {
        domains: [],
        plugin: [],
        externalPlugin: [],
        nfts: [],
        erc20Tokens: []
      };
      const transport = await this.#transport;
      const obj = await new Eth(transport).signTransaction(this.#path, rawTx, resolution);
      obj.v = "0x" + obj.v;
      obj.r = "0x" + obj.r;
      obj.s = "0x" + obj.s;
      tx.signature = obj;
      return tx.serialized;
    } catch (error) {
      throw error;
    }
  }
  async signMessage(message) {
    if (typeof message === "string") {
      message = toUtf8Bytes(message);
    }
    try {
      const transport = await this.#transport;
      const obj = await new Eth(transport).signPersonalMessage(
        this.#path,
        hexlify(message).substring(2)
      );
      obj.r = "0x" + obj.r;
      obj.s = "0x" + obj.s;
      return Signature.from(obj).serialized;
    } catch (error) {
      throw error;
    }
  }
  async signTypedData(domain, types, value) {
    const populated = await TypedDataEncoder.resolveNames(
      domain,
      types,
      value,
      async (name) => await resolveAddress(name, this.provider)
    );
    try {
      const transport = await this.#transport;
      const eth = new Eth(transport);
      const payload = TypedDataEncoder.getPayload(populated.domain, types, populated.value);
      let obj;
      try {
        obj = await eth.signEIP712Message(this.#path, payload);
      } catch (error) {
        if (!error || error.statusCode !== 27904) {
          throw error;
        }
        const domainHash = TypedDataEncoder.hashDomain(domain);
        const valueHash = TypedDataEncoder.from(types).hash(value);
        try {
          obj = await eth.signEIP712HashedMessage(
            this.#path,
            domainHash.substring(2),
            valueHash.substring(2)
          );
        } catch (error2) {
          throw error2;
        }
      }
      obj.r = "0x" + obj.r;
      obj.s = "0x" + obj.s;
      return Signature.from(obj).serialized;
    } catch (error) {
      throw error;
    }
  }
  /**
   *  Returns the HD %%path%%. If unspecified, returns the default
   *  path (i.e. ``m/44'/60'/0'/0/0``), if a ``number``, the path
   *  is for that account using the BIP-44 standard, otherwise %%path%%
   *  is returned directly.
   */
  static getPath(path) {
    if (path == null) {
      path = 0;
    }
    if (typeof path === "number") {
      return getAccountPath(path);
    }
    return path;
  }
};

// src/utils/testUtils.ts
var mockImplicitContact = (index, label, chainId = ChainFullIds.TEZOS) => ({
  name: label || `Contact ${index}`,
  pkh: mockImplicitAddressByChainId(index, chainId),
  network: void 0,
  chainNamespace: parseChainFullId(chainId).namespace
});
var mockImplicitAddressByChainId = (index, chainId) => {
  const { namespace } = parseChainFullId(chainId);
  switch (namespace) {
    case ChainNamespaces.TEZOS:
      return mockImplicitAddress(index).pkh;
    case ChainNamespaces.EVM:
      return mockEvmCompatibleImplicitAddress().pkh;
    case ChainNamespaces.BITCOIN:
      return mockBitcoinCompatibleImplicitAddress(index);
    default:
      throw new Error(`Unsupported chain namespace: ${namespace}`);
  }
};

// src/utils/keyConversion.ts
import { CustomError as CustomError16 } from "@umami/utils";
import bs58check from "bs58check";
import { ethers as ethers2 } from "ethers";
var SPSK_PREFIX = Buffer.from([17, 162, 224, 201]);
var EVM_PRIVATE_KEY_LENGTH = 64;
var CHECKSUM_LENGTH = 4;
var TEZOS_KEY_PREFIXES = {
  EDSK: "edsk",
  SPSK: "spsk",
  P2SK: "p2sk"
};
var EVM_PREFIX = "0x";
var SecretKeyTypes = {
  TEZOS_SECRET_KEY: "tezos_secret_key",
  EVM_PRIVATE_KEY: "evm_private_key",
  UNKNOWN: "unknown"
};
var ERROR_MESSAGES = {
  INVALID_EVM_FORMAT: "Invalid EVM private key format. Must be a 64-character hex string with or without 0x prefix",
  CONVERSION_FAILED: "Failed to convert EVM private key to Tezos spsk",
  INVALID_TEZOS_FORMAT: 'Tezos keys which can be converted must start with "spsk"',
  NON_CONVERTIBLE_TEZOS: "Only Tezos spsk keys can be used for multichain wallets. Key format cannot be converted to EVM format",
  UNSUPPORTED_FORMAT: "Unsupported secret key format. Please provide valid secret key"
};
var trimKey = (key) => key.trim();
var removeEvmPrefix = (key) => key.startsWith(EVM_PREFIX) ? key.slice(2) : key;
var ensureEvmPrefix = (key) => key.startsWith(EVM_PREFIX) ? key : `${EVM_PREFIX}${key}`;
var isTezosKeyFormat = (key) => Object.values(TEZOS_KEY_PREFIXES).some((prefix) => key.startsWith(prefix));
var isValidHex = (hex, expectedLength) => {
  const hexWithPrefix = hex.startsWith("0x") ? hex : `0x${hex}`;
  return ethers2.isHexString(hexWithPrefix, expectedLength / 2);
};
var isValidEvmFormat = (key) => {
  const withoutPrefix = removeEvmPrefix(key);
  return isValidHex(withoutPrefix, EVM_PRIVATE_KEY_LENGTH);
};
var createConversionError = (message, originalKey, cause) => {
  const errorMessage = originalKey ? `${message}: ${originalKey}` : message;
  if (cause instanceof Error) {
    return new CustomError16(`${errorMessage}. ${cause.message}`);
  }
  return new CustomError16(errorMessage);
};
var detectSecretKeyType = (key) => {
  const trimmedKey = trimKey(key);
  if (isTezosKeyFormat(trimmedKey)) {
    return SecretKeyTypes.TEZOS_SECRET_KEY;
  }
  if (isValidEvmFormat(trimmedKey)) {
    return SecretKeyTypes.EVM_PRIVATE_KEY;
  }
  return SecretKeyTypes.UNKNOWN;
};
var isValidEvmPrivateKey = (key) => {
  try {
    const trimmedKey = trimKey(key);
    if (!isValidEvmFormat(trimmedKey)) {
      return false;
    }
    const normalizedKey = ensureEvmPrefix(removeEvmPrefix(trimmedKey));
    const wallet = new ethers2.Wallet(normalizedKey);
    return !!wallet.address;
  } catch {
    return false;
  }
};
var convertEvmPrivateKeyToTezosSpsk = (privateKey) => {
  const trimmedKey = trimKey(privateKey);
  if (!isValidEvmPrivateKey(trimmedKey)) {
    throw createConversionError(ERROR_MESSAGES.INVALID_EVM_FORMAT, privateKey);
  }
  try {
    const hexKey = removeEvmPrefix(trimmedKey);
    const privateKeyBuffer = Buffer.from(hexKey, "hex");
    const prefixedKey = Buffer.concat([SPSK_PREFIX, privateKeyBuffer]);
    return bs58check.encode(prefixedKey);
  } catch (error) {
    throw createConversionError(ERROR_MESSAGES.CONVERSION_FAILED, privateKey, error);
  }
};
var convertTezosSpskToEvmPrivateKey = (key) => {
  const trimmedKey = trimKey(key);
  if (trimmedKey.startsWith(EVM_PREFIX)) {
    return trimmedKey;
  }
  if (!trimmedKey.startsWith(TEZOS_KEY_PREFIXES.SPSK)) {
    throw createConversionError(ERROR_MESSAGES.INVALID_TEZOS_FORMAT, key);
  }
  try {
    const base58Decoded = ethers2.decodeBase58(trimmedKey);
    const bytesArray = ethers2.toBeArray(base58Decoded);
    const rawPrivateKey = bytesArray.slice(
      TEZOS_KEY_PREFIXES.SPSK.length,
      bytesArray.length - CHECKSUM_LENGTH
    );
    return ethers2.toQuantity(rawPrivateKey);
  } catch (error) {
    throw createConversionError(ERROR_MESSAGES.INVALID_TEZOS_FORMAT, key, error);
  }
};
var normalizeTezosKey = async (key) => {
  try {
    await decryptSecretKey(key);
    if (key.startsWith(TEZOS_KEY_PREFIXES.SPSK)) {
      const evmKey = convertTezosSpskToEvmPrivateKey(key);
      const btcKey = removeEvmPrefix(evmKey);
      return {
        tezos: key,
        multichain: {
          eip155: evmKey,
          bitcoin: btcKey
        }
      };
    }
    return {
      tezos: key,
      // TODO: remove typecasting
      multichain: {}
    };
  } catch (error) {
    throw createConversionError(ERROR_MESSAGES.UNSUPPORTED_FORMAT, key, error);
  }
};
var normalizeEvmKey = (key) => {
  const tezosSpsk = convertEvmPrivateKeyToTezosSpsk(key);
  const evmKey = ensureEvmPrefix(key);
  const btcKey = removeEvmPrefix(evmKey);
  return {
    tezos: tezosSpsk,
    multichain: {
      eip155: evmKey,
      bitcoin: btcKey
    }
  };
};
var normalizeSecretKey = async (secretKey) => {
  const keyType = detectSecretKeyType(secretKey);
  const trimmedKey = trimKey(secretKey);
  switch (keyType) {
    case SecretKeyTypes.TEZOS_SECRET_KEY:
      return await normalizeTezosKey(trimmedKey);
    case SecretKeyTypes.EVM_PRIVATE_KEY:
      return normalizeEvmKey(trimmedKey);
    case SecretKeyTypes.UNKNOWN:
    default:
      throw createConversionError(ERROR_MESSAGES.UNSUPPORTED_FORMAT, secretKey);
  }
};

// src/utils/normalizer.ts
var getTransactionDirection2 = (userAddress, fromAddress, toAddress) => {
  const isSender = userAddress.toLowerCase() === fromAddress.toLowerCase();
  if (toAddress === void 0) {
    return isSender ? "send" : "receive";
  }
  const isReceiver = userAddress.toLowerCase() === toAddress.toLowerCase();
  if (isSender && !isReceiver) {
    return "send";
  }
  if (!isSender && isReceiver) {
    return "receive";
  }
  return isSender ? "send" : "receive";
};
var getOperationType = (op) => {
  switch (op.type) {
    case "transaction":
      return OperationTypes.TRANSACTION;
    case "token_transfer":
      return OperationTypes.TOKEN_TRANSFER;
    case "safe_send":
      return OperationTypes.SAFE_SEND;
    case "delegation":
    case "origination":
    case "stake":
    case "unstake":
    case "finalize":
      return OperationTypes.CONTRACT_CALL;
    default:
      return OperationTypes.TRANSACTION;
  }
};
var getTotalFee = (op) => (op.bakerFee || 0) + (op.storageFee || 0) + (op.allocationFee || 0);
var normalizeTezosToMultichainOp = (op, userAddress, explorerUrl, saplingContractAddress) => {
  if (op.type === "token_transfer") {
    const tokenTransfer = op;
    const from = tokenTransfer.from?.address || "";
    const to = tokenTransfer.to?.address || "";
    const hash = tokenTransfer.transactionId?.toString() || op.id.toString();
    const iconUrl = getIPFSurl(
      tokenTransfer.token.metadata?.thumbnailUri || tokenTransfer.token.metadata?.displayUri || tokenTransfer.token.metadata?.artifactUri
    );
    const isNft = NFTSchema.safeParse(tokenTransfer.token).success;
    return {
      hash,
      from,
      to,
      amount: isNft ? tokenTransfer.amount : formatToNormalUnit(tokenTransfer.amount, ChainFullIds.TEZOS),
      timestamp: tokenTransfer.timestamp,
      explorerUrl: `${explorerUrl}/transactions/${hash}`,
      id: op.id.toString(),
      chainId: ChainFullIds.TEZOS,
      operationType: getOperationType(op),
      ticker: isNft ? tokenTransfer.token.metadata?.name : CryptoSymbols[ChainIds.TEZOS],
      txDirection: getTransactionDirection2(userAddress, from, to),
      isNft,
      token: {
        name: tokenTransfer.token.metadata?.name,
        symbol: tokenTransfer.token.metadata?.symbol,
        decimals: tokenTransfer.token.metadata?.decimals,
        ...tokenTransfer.token.metadata
      },
      iconUrl
    };
  }
  const base = {
    hash: op.hash,
    timestamp: op.timestamp,
    explorerUrl: `${explorerUrl}/${op.hash}`,
    id: op.id.toString(),
    chainId: ChainFullIds.TEZOS,
    operationType: getOperationType(op),
    ticker: CryptoSymbols[ChainIds.TEZOS],
    status: "status" in op ? op.status : void 0
  };
  switch (op.type) {
    case "transaction": {
      const tx = op;
      const from = tx.sender.address || "";
      const to = tx.target?.address || "";
      const isShield = saplingContractAddress && to === saplingContractAddress;
      const isUnshield = saplingContractAddress && from === saplingContractAddress;
      const result = {
        ...base,
        from,
        to,
        amount: formatToNormalUnit(tx.amount.toString(), ChainFullIds.TEZOS),
        fee: getTotalFee(tx).toString(),
        txDirection: getTransactionDirection2(userAddress, from, to),
        isContractCall: !!tx.parameter
      };
      if (isShield) {
        return {
          ...result,
          operationType: OperationTypes.SHIELD
        };
      }
      if (isUnshield) {
        return {
          ...result,
          operationType: OperationTypes.UNSHIELD
        };
      }
      return result;
    }
    case "delegation": {
      const delegation = op;
      const from = delegation.sender.address || "";
      const to = delegation.newDelegate?.address || "";
      return {
        ...base,
        from,
        to,
        amount: formatToNormalUnit(delegation.amount.toString(), ChainFullIds.TEZOS),
        fee: getTotalFee(delegation).toString(),
        txDirection: getTransactionDirection2(userAddress, from, to)
      };
    }
    case "origination": {
      const origination = op;
      const from = origination.sender.address;
      const to = origination.originatedContract?.address || "";
      return {
        ...base,
        from,
        to,
        amount: "0",
        fee: getTotalFee(origination).toString(),
        txDirection: getTransactionDirection2(userAddress, from, to)
      };
    }
    case "stake": {
      const stake = op;
      const from = stake.sender.address;
      const to = stake.baker.address;
      return {
        ...base,
        from,
        to,
        amount: formatToNormalUnit(stake.amount.toString(), ChainFullIds.TEZOS),
        fee: getTotalFee(stake).toString(),
        txDirection: getTransactionDirection2(userAddress, from, to)
      };
    }
    case "unstake": {
      const unstake = op;
      const from = unstake.sender.address;
      const to = unstake.baker.address;
      return {
        ...base,
        from,
        to,
        amount: formatToNormalUnit(unstake.amount.toString(), ChainFullIds.TEZOS),
        fee: getTotalFee(unstake).toString(),
        txDirection: getTransactionDirection2(userAddress, from, to)
      };
    }
    case "finalize": {
      const finalizeUnstake = op;
      const from = finalizeUnstake.sender.address;
      return {
        ...base,
        from,
        to: from,
        amount: formatToNormalUnit(finalizeUnstake.amount.toString(), ChainFullIds.TEZOS),
        fee: getTotalFee(finalizeUnstake).toString(),
        txDirection: getTransactionDirection2(userAddress, from)
      };
    }
    case "safe_send": {
      const safeSend = op;
      let from;
      let to;
      switch (safeSend.action) {
        case "send":
          from = safeSend.sender.address;
          to = safeSend.target?.address ?? "";
          break;
        case "withdraw":
        case "claim":
          from = safeSend.target?.address ?? "";
          to = safeSend.sender.address;
          break;
      }
      return {
        ...base,
        from,
        to,
        amount: formatToNormalUnit(safeSend.amount.toString(), ChainFullIds.TEZOS),
        fee: getTotalFee(safeSend).toString(),
        txDirection: getTransactionDirection2(userAddress, from, to),
        action: safeSend.action,
        assetType: safeSend.assetType,
        safeSendStatus: safeSend.safeSendStatus,
        parameter: safeSend.parameter
      };
    }
    default:
      return {};
  }
};

// src/providers/utils.ts
var USER_FRIENDLY_ERRORS = [
  {
    keywords: ["404", "not found"],
    message: "Unable to find the requested information. Please try again later."
  },
  {
    keywords: ["500", "internal server error"],
    message: "Service is temporarily unavailable. Please try again in a few moments."
  },
  {
    keywords: ["503", "service unavailable"],
    message: "Service is currently under maintenance. Please try again later."
  },
  {
    keywords: ["timeout", "network"],
    message: "Network connection issue. Please check your internet connection and try again."
  },
  {
    keywords: ["rate limit", "too many requests"],
    message: "Too many requests. Please wait a moment and try again."
  },
  {
    keywords: ["transaction history"],
    message: "Unable to load transaction history at the moment."
  },
  {
    keywords: ["tokens transfers"],
    message: "Unable to load token transfers at the moment."
  },
  {
    keywords: ["tokens"],
    message: "Unable to load token balances at the moment."
  },
  {
    keywords: ["nft"],
    message: "Unable to load NFT collection at the moment."
  },
  {
    keywords: ["balance"],
    message: "Unable to load wallet balance at the moment."
  },
  {
    keywords: ["gas", "fee"],
    message: "Unable to estimate transaction fees. Please try again."
  }
];
var formatUserFriendlyError = (errorMessage) => {
  const lowerMessage = errorMessage.toLowerCase();
  const matchedPattern = USER_FRIENDLY_ERRORS.find(
    (pattern) => pattern.keywords.some((keyword) => lowerMessage.includes(keyword))
  );
  return matchedPattern?.message ?? errorMessage;
};

// src/providers/BaseProviders/BaseProvider.ts
var BaseProvider = class {
  /**
   * Network configuration
   */
  config;
  /**
   * Returns the name identifier of the blockchain network
   */
  get chainName() {
    return this.config.name;
  }
  /**
   * Returns the full ID of the blockchain network
   */
  get chainFullId() {
    return getChainFullIdFromNetwork(this.config);
  }
  /**
   * Helpers and reusable methods
   */
  getAddress() {
    this.validateAddress();
    return this.address;
  }
  getExplorerUrl() {
    return this.config.explorerUrl;
  }
  validateAddress() {
    if (!this.address) {
      throw new CustomError17("Provider not properly configured. Address is empty");
    }
  }
  handleError(errorMessage, error) {
    try {
      const chainDisplayName = this.chainName.charAt(0).toUpperCase() + this.chainName.slice(1);
      const messageToFormat = error instanceof CustomError17 ? error.message : errorMessage;
      const userFriendlyMessage = formatUserFriendlyError(messageToFormat);
      const chainPrefix = `${chainDisplayName}: `;
      const finalMessage = userFriendlyMessage.startsWith(chainPrefix) ? userFriendlyMessage : `${chainPrefix}${userFriendlyMessage}`;
      throw new CustomError17(finalMessage);
    } finally {
      console.error(error);
    }
  }
  async fetchAndNormalizeData({
    path,
    searchParams,
    fetchFn,
    normalizeFn
  }) {
    try {
      this.validateAddress();
      const url = this.getExplorerAddressUrl(path);
      Object.entries(searchParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
      const data = await fetchFn(url);
      const items = "result" in data ? data.result : data.items;
      return items.map(normalizeFn);
    } catch (error) {
      this.handleError("Failed to fetch data", error);
    }
  }
};

// src/providers/BaseProviders/BaseEvmProvider.ts
var BaseEvmProvider = class extends BaseProvider {
  /**
   * Helpers and reusable methods
   */
  validateProvider() {
    if (!this.provider) {
      throw new CustomError18("Provider not initialized. Provider is null");
    }
  }
  validateAddress() {
    this.validateProvider();
    super.validateAddress();
  }
};

// src/providers/evm/EvmProvider.ts
import TransportWebUSB2 from "@ledgerhq/hw-transport-webusb";
import { CustomError as CustomError19 } from "@umami/utils";
import {
  Wallet as EthersWallet,
  HDNodeWallet,
  Interface,
  JsonRpcProvider,
  ethers as ethers3,
  formatEther as formatEther2,
  parseEther as parseEther2
} from "ethers";
var EvmProvider = class extends BaseEvmProvider {
  provider;
  wallet = null;
  address = "";
  erc20Interface = new Interface([
    "function transfer(address to, uint256 amount)"
  ]);
  erc721Interface = new Interface([
    "function safeTransferFrom(address from, address to, uint256 tokenId)"
  ]);
  erc1155Interface = new Interface([
    "function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data)"
  ]);
  constructor(config, params) {
    super();
    this.address = params?.address.pkh ?? "";
    this.config = config;
    this.initializeProvider(config);
  }
  initializeProvider(config) {
    try {
      this.provider = new JsonRpcProvider(config.rpcUrl);
    } catch (error) {
      this.handleError("Failed to initialize provider", error);
    }
  }
  getExplorerAddressUrl(path) {
    return new URL(`${this.getExplorerUrl()}/api/v2/addresses/${this.address}${path}`);
  }
  getExplorerUrl() {
    return this.config.explorerProxyUrl ?? this.config.explorerUrl;
  }
  createWallet(options) {
    try {
      const canCreateWalletFromAddress = "address" in options;
      let pk;
      let derivationPath;
      if (canCreateWalletFromAddress) {
        pk = options.pk;
        derivationPath = options.derivationPath;
        this.address = options.address;
      } else {
        this.wallet = this.initWalletFromOptions(options);
        this.address = this.wallet.address;
        pk = this.wallet.signingKey.publicKey;
        derivationPath = "derivationPaths" in options ? options.derivationPaths[this.chainFullId] : void 0;
      }
      return {
        chainId: this.chainFullId,
        address: { type: "implicit", pkh: this.address },
        pk,
        derivationPath,
        decimals: this.config.decimals
      };
    } catch (error) {
      this.handleError("Failed to create wallet", error);
    }
  }
  async estimate(transaction) {
    try {
      this.validateProvider();
      const tx = this.buildTransactionRequest(transaction);
      const gasLimit = await this.provider.estimateGas(tx);
      const feeData = await this.provider.getFeeData();
      const estimatedFee = feeData.gasPrice ? gasLimit * feeData.gasPrice : BigInt(0);
      return {
        fee: Number(estimatedFee),
        gasLimit: Number(gasLimit)
      };
    } catch (error) {
      this.handleError("Failed to estimate transaction", error);
    }
  }
  async signTransaction(transaction, options) {
    try {
      if ("isLedger" in options && options.isLedger) {
        const signer = await this.getLedgerSigner({ derivationPath: options.derivationPath });
        const signedTx2 = await signer.signTransaction(transaction);
        await signer.close();
        return {
          ...transaction,
          hash: signedTx2
        };
      }
      this.wallet = this.initWalletFromOptions(options);
      const signedTx = await this.wallet.signTransaction(transaction);
      return {
        ...transaction,
        hash: signedTx
      };
    } catch (error) {
      this.handleError("Failed to sign transaction", error);
    }
  }
  async signMessage(message, options) {
    try {
      this.wallet = this.initWalletFromOptions(options);
      const messageStr = message.startsWith("0x") ? ethers3.toUtf8String(message) : message;
      const signedMsg = await this.wallet.signMessage(messageStr);
      return signedMsg;
    } catch (error) {
      this.handleError("Failed to sign message", error);
    }
  }
  async signTypedData(typedData, options) {
    try {
      this.wallet = this.initWalletFromOptions(options);
      const signature = await this.wallet.signTypedData(
        typedData.domain,
        typedData.types,
        typedData.message
      );
      return signature;
    } catch (error) {
      this.handleError("Failed to sign typed data", error);
    }
  }
  async signAndSendTransaction(transaction, options) {
    try {
      if ("isLedger" in options && options.isLedger) {
        const signer = await this.getLedgerSigner({ derivationPath: options.derivationPath });
        const tx2 = this.buildTransactionRequest(transaction);
        const sentTx = await signer.sendTransaction(tx2);
        await signer.close();
        return sentTx;
      }
      this.wallet = this.initWalletFromOptions(options);
      const tx = this.buildTransactionRequest(transaction);
      return await this.wallet.sendTransaction(tx);
    } catch (error) {
      this.handleError("Failed to sign and send transaction", error);
    }
  }
  async getBalance() {
    const commonFields = {
      chainId: this.chainFullId,
      symbol: this.config.symbol
    };
    try {
      this.validateAddress();
      const balanceWei = await this.provider.getBalance(this.address);
      const balanceEther = formatEther2(balanceWei);
      return {
        total: balanceEther,
        available: balanceEther,
        ...commonFields
      };
    } catch (error) {
      if (error instanceof Error && error.message.includes("Balance not found")) {
        return {
          total: "0",
          available: "0",
          ...commonFields
        };
      } else {
        this.handleError("Failed to get balance", error);
      }
    }
  }
  buildTransactionRequest(transaction) {
    try {
      const tx = {
        to: transaction.to,
        value: transaction.value,
        data: transaction.data
      };
      if (transaction.contract && !transaction.data) {
        if (transaction.tokenType) {
          if (transaction.tokenType === "ERC-721") {
            tx.data = this.erc721Interface.encodeFunctionData("safeTransferFrom", [
              this.address,
              transaction.to,
              transaction.tokenId
            ]);
            tx.to = transaction.contract;
            tx.from = this.address;
            tx.value = parseEther2("0");
          } else if (transaction.tokenType === "ERC-1155") {
            tx.data = this.erc1155Interface.encodeFunctionData("safeTransferFrom", [
              this.address,
              transaction.to,
              transaction.tokenId,
              transaction.value,
              "0x"
            ]);
            tx.to = transaction.contract;
            tx.from = this.address;
            tx.value = parseEther2("0");
          }
        } else {
          tx.data = this.erc20Interface.encodeFunctionData("transfer", [
            transaction.to,
            transaction.value
          ]);
          tx.to = transaction.contract;
          tx.from = this.address;
          tx.value = parseEther2("0");
        }
      }
      return tx;
    } catch (error) {
      this.handleError("Failed to build transaction request", error);
    }
  }
  initWalletFromOptions(options) {
    try {
      this.validateProvider();
      const canCreateWalletFromSecretKey = "secretKey" in options;
      const canCreateWalletFromMnemonic = "mnemonic" in options;
      if (canCreateWalletFromSecretKey && options.secretKey) {
        return new EthersWallet(options.secretKey, this.provider);
      } else if (canCreateWalletFromMnemonic && options.mnemonic) {
        return HDNodeWallet.fromPhrase(
          options.mnemonic,
          "",
          options.derivationPaths?.[this.chainFullId]
        ).connect(this.provider);
      } else {
        throw new CustomError19("No valid mnemonic or secret key provided for wallet initialization");
      }
    } catch (error) {
      this.handleError("Failed to initialize wallet", error);
    }
  }
  async getTxHistory() {
    const txs = await this.fetchAndNormalizeData({
      path: "/transactions",
      searchParams: {},
      fetchFn: fetchTxHistory,
      normalizeFn: (tx) => normalizeEvmTx(this.chainFullId, this.config)(tx, this.address)
    });
    return txs.filter((tx) => !tx.isContractCall);
  }
  async getTokensTransfers() {
    return this.fetchAndNormalizeData({
      path: "/token-transfers",
      searchParams: {},
      fetchFn: fetchTokensTransfers,
      normalizeFn: (tx) => normalizeEvmTokenTransfer(this.chainFullId, this.config)(tx, this.address)
    });
  }
  async getTokens() {
    return this.fetchAndNormalizeData({
      path: "/tokens",
      searchParams: { type: "ERC-20" },
      fetchFn: fetchTokens,
      normalizeFn: normalizeEvmToken(this.chainFullId)
    });
  }
  async getNfts() {
    return this.fetchAndNormalizeData({
      path: "/nft",
      searchParams: { type: "ERC-721,ERC-1155" },
      fetchFn: fetchNfts,
      normalizeFn: normalizeEvmNFT(this.chainFullId)
    });
  }
  async getAddressIconUrl(address3) {
    try {
      this.validateProvider();
      const response = await fetch(`${this.getExplorerUrl()}/api/v2/addresses/${address3}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch address info: ${response.status}`);
      }
      const data = await response.json();
      if (data.token && data.token.icon_url) {
        return data.token.icon_url;
      }
      return "";
    } catch (error) {
      this.handleError("Failed to get address icon URL", error);
    }
  }
  async getLedgerSigner({ derivationPath }) {
    const transport = await TransportWebUSB2.create();
    return new LedgerSigner2(transport, this.provider, derivationPath);
  }
  disconnect() {
    this.provider.destroy();
  }
};

// src/providers/bitcoin/BitcoinProvider.ts
import * as ecc from "@bitcoinerlab/secp256k1";
import { default as Btc } from "@ledgerhq/hw-app-btc";
import TransportWebUSB3 from "@ledgerhq/hw-transport-webusb";
import { CustomError as CustomError21 } from "@umami/utils";
import { BIP32Factory } from "bip32";
import { mnemonicToSeedSync } from "bip39";
import * as bitcoin3 from "bitcoinjs-lib";
import ECPairFactory from "ecpair";

// src/providers/bitcoin/fetch.ts
var fetchBalance = (url) => genericFetch2(url, "Failed to get balance");
var fetchTxHistory2 = (url) => genericFetch2(url, "Failed to get transaction history");
var fetchFeeEstimates = (url) => genericFetch2(url, "Failed to get fee estimates");
var fetchUTXOs = (url) => genericFetch2(url, "Failed to get UTXOs");
var broadcastTx = (url, options) => genericFetch2(url, "Failed to broadcast transaction", options);

// src/providers/bitcoin/utils/transaction.ts
import { CustomError as CustomError20 } from "@umami/utils";
import * as bitcoin2 from "bitcoinjs-lib";
var serializeTxToHex = (tx) => {
  const t = new bitcoin2.Transaction();
  t.version = tx.version;
  t.locktime = tx.locktime;
  tx.vin.forEach((input) => {
    t.addInput(
      Buffer.from(input.txid, "hex").reverse(),
      input.vout,
      input.sequence,
      input.scriptsig ? Buffer.from(input.scriptsig, "hex") : Buffer.alloc(0)
    );
  });
  tx.vout.forEach((output) => {
    t.addOutput(Buffer.from(output.scriptpubkey, "hex"), output.value);
  });
  tx.vin.forEach((input, idx) => {
    if (input.witness.length > 0) {
      t.setWitness(
        idx,
        input.witness.map((w) => Buffer.from(w, "hex"))
      );
    }
  });
  return t.toHex();
};
var getOutputScriptHex = (address3, network, outputs) => {
  const unsignedTx = new bitcoin2.Transaction();
  unsignedTx.version = 2;
  for (const o of outputs) {
    const outAddr = o.address ?? address3;
    const script = bitcoin2.address.toOutputScript(outAddr, network);
    unsignedTx.addOutput(script, o.value);
  }
  const newTxHex = unsignedTx.toHex();
  const outputsStart = (4 + 1) * 2;
  const outputsEnd = newTxHex.length - 8;
  if (outputsEnd <= outputsStart) {
    throw new CustomError20("Constructed transaction outputs are invalid");
  }
  return newTxHex.slice(outputsStart, outputsEnd);
};
var createPaymentTx = async ({
  btc,
  utxoSelection,
  derivationPath,
  address: address3,
  network,
  txs,
  addressType
}) => {
  const { inputs, outputs, confirmedUtxos: utxos } = utxoSelection;
  const txsMap = new Map(txs.map((tx) => [tx.txid, tx]));
  const splitPrevTxs = inputs.map((i) => {
    const prev = utxos.find((u) => u.txid === i.txid && u.vout === i.vout);
    if (!prev) {
      throw new CustomError20("Failed to locate UTXO details for Ledger signing");
    }
    const prevTx = txsMap.get(i.txid);
    if (!prevTx) {
      throw new CustomError20(`Failed to locate tx ${i.txid}`);
    }
    const rawHex = serializeTxToHex(prevTx);
    if (!rawHex) {
      throw new CustomError20(`Empty raw hex for tx ${i.txid}`);
    }
    const split = btc.splitTransaction(rawHex, true, false);
    const DEFAULT_SEQUENCE = 4294967295;
    return [split, i.vout, void 0, DEFAULT_SEQUENCE];
  });
  const outputScriptHex = getOutputScriptHex(address3, network, outputs);
  try {
    return await btc.createPaymentTransaction({
      inputs: splitPrevTxs,
      associatedKeysets: Array(inputs.length).fill(derivationPath),
      outputScriptHex,
      changePath: outputs.some((o) => o.address === address3) ? derivationPath : void 0,
      lockTime: 0,
      sigHashType: 1,
      segwit: true,
      additionals: addressType === BITCOIN_ADDRESS_TYPES.P2WPKH || addressType === BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH ? ["bech32"] : [],
      useTrustedInputForSegwit: true
    });
  } catch (err) {
    throw new CustomError20(`Ledger signing failed: ${err.message}`);
  }
};
var preparePsbtTx = ({
  utxoSelection,
  addressType,
  keyPair,
  network,
  fallbackAddress
}) => {
  const { inputs, outputs, confirmedUtxos: utxos } = utxoSelection;
  const psbt = new bitcoin2.Psbt({ network });
  const pubkeyBuffer = Buffer.from(keyPair.publicKey);
  const payments3 = getPayments(pubkeyBuffer, network);
  const utxoMap = new Map(utxos.map((u) => [`${u.txid}:${u.vout}`, u]));
  for (const input of inputs) {
    const key = `${input.txid}:${input.vout}`;
    const source = utxoMap.get(key);
    if (!source) {
      throw new CustomError20("Failed to locate UTXO details for signing");
    }
    const inputData = {
      hash: input.txid,
      index: input.vout,
      witnessUtxo: { script: payments3[addressType].output, value: source.value }
    };
    if (addressType === BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH) {
      inputData.redeemScript = payments3[BITCOIN_ADDRESS_TYPES.P2SH_P2WPKH].redeem?.output;
    }
    if (addressType === BITCOIN_ADDRESS_TYPES.P2TR) {
      inputData.tapInternalKey = payments3[BITCOIN_ADDRESS_TYPES.P2TR].internalPubkey;
    }
    psbt.addInput(inputData);
  }
  for (const output of outputs) {
    psbt.addOutput({ address: output.address ?? fallbackAddress, value: output.value });
  }
  if (addressType === BITCOIN_ADDRESS_TYPES.P2TR) {
    signAllTaprootInputs({ psbt, keyPair, network });
  } else {
    psbt.signAllInputs({
      publicKey: pubkeyBuffer,
      sign: (hash) => Buffer.from(keyPair.sign(hash))
    });
  }
  psbt.finalizeAllInputs();
  return psbt.extractTransaction().toHex();
};
var makeTxResponse = (txid, address3, value) => ({
  hash: txid,
  to: null,
  from: address3,
  data: "",
  value: BigInt(value || "0"),
  nonce: 0,
  gasLimit: 0n,
  gasPrice: 0n,
  chainId: 0n,
  type: 0,
  confirmations: 0,
  blockNumber: null,
  blockHash: null,
  index: 0,
  wait: () => Promise.resolve({ status: 1, logs: [] })
});

// src/providers/bitcoin/BitcoinProvider.ts
var blockExplorerUrls = {
  [ChainFullIds.BITCOIN]: "https://blockstream.info",
  [ChainFullIds.BITCOIN_TESTNET]: "https://blockstream.info/testnet"
};
var ECPair = ECPairFactory(ecc);
bitcoin3.initEccLib(ecc);
var BitcoinProvider = class extends BaseProvider {
  wallet = null;
  address = "";
  network;
  constructor(config, params) {
    super();
    this.address = params?.address.pkh ?? "";
    this.config = config;
    this.network = config.identifier === "0" ? bitcoin3.networks.bitcoin : bitcoin3.networks.testnet;
  }
  getExplorerAddressUrl(path) {
    return new URL(`${this.config.explorerUrl}${path}`);
  }
  createWallet(options) {
    try {
      const canCreateWalletFromAddress = "address" in options;
      let pk;
      let derivationPath;
      if (canCreateWalletFromAddress) {
        pk = options.pk;
        derivationPath = options.derivationPath;
        const pkBuffer = Buffer.from(pk, "hex");
        const addressType = getAddressType(options.address);
        const { address: actualAddress } = getPayments(pkBuffer, this.network)[addressType];
        if (!actualAddress) {
          throw new CustomError21("Failed to derive address from public key");
        }
        this.address = actualAddress;
      } else {
        this.wallet = this.initWalletFromOptions(options);
        this.address = this.wallet.address;
        pk = this.wallet.publicKey;
        derivationPath = "derivationPaths" in options ? options.derivationPaths[this.chainFullId] : defaultDerivationPath[this.config.identifier];
      }
      return {
        chainId: this.chainFullId,
        address: { type: "implicit", pkh: this.address },
        pk,
        derivationPath,
        decimals: this.config.decimals
      };
    } catch (error) {
      this.handleError("Failed to create wallet", error);
    }
  }
  initWalletFromOptions(options) {
    try {
      const canCreateWalletFromSecretKey = "secretKey" in options;
      const canCreateWalletFromMnemonic = "mnemonic" in options;
      if (canCreateWalletFromSecretKey && options.secretKey) {
        return this.initSecretKeyWallet(options.secretKey);
      } else if (canCreateWalletFromMnemonic && options.mnemonic) {
        return this.initMnemonicWallet({
          mnemonic: options.mnemonic,
          derivationPaths: options.derivationPaths
        });
      } else {
        throw new CustomError21("No valid mnemonic or secret key provided for wallet initialization");
      }
    } catch (error) {
      this.handleError("Failed to initialize wallet", error);
    }
  }
  initMnemonicWallet(options) {
    const seed = mnemonicToSeedSync(options.mnemonic);
    const root = BIP32Factory(ecc).fromSeed(seed, this.network);
    const derivationPath = options.derivationPaths?.[this.chainFullId] ?? defaultDerivationPath[this.config.identifier];
    const node = root.derivePath(derivationPath);
    if (!node.privateKey) {
      throw new CustomError21("Failed to derive private key from mnemonic");
    }
    const keyPair = ECPair.fromPrivateKey(node.privateKey, {
      network: this.network
    });
    const addressType = getAddressTypeFromPath(derivationPath);
    const { address: address3 } = getPayments(Buffer.from(keyPair.publicKey), this.network)[addressType];
    if (!address3) {
      throw new CustomError21("Failed to derive address from mnemonic");
    } else if (!keyPair.privateKey) {
      throw new CustomError21("Failed to derive private key from mnemonic");
    }
    return {
      address: address3,
      publicKey: Buffer.from(keyPair.publicKey).toString("hex"),
      privateKey: Buffer.from(keyPair.privateKey).toString("hex"),
      keyPair
    };
  }
  initSecretKeyWallet(secretKey) {
    const keyPair = ECPair.fromPrivateKey(Buffer.from(secretKey, "hex"), {
      network: this.network
    });
    const { address: address3 } = bitcoin3.payments.p2wpkh({
      pubkey: Buffer.from(keyPair.publicKey),
      network: this.network
    });
    if (!address3) {
      throw new CustomError21("Failed to derive address from secret key");
    } else if (!keyPair.privateKey) {
      throw new CustomError21("Failed to derive private key from secret key");
    }
    return {
      address: address3,
      publicKey: Buffer.from(keyPair.publicKey).toString("hex"),
      privateKey: Buffer.from(keyPair.privateKey).toString("hex"),
      keyPair
    };
  }
  async estimate(transaction) {
    try {
      this.validateAddress();
      const { fee } = await this.prepareSelection(transaction);
      return { fee, gasLimit: 0 };
    } catch (error) {
      this.handleError("Failed to estimate Bitcoin transaction", error);
    }
  }
  async signTransaction(_transaction, options) {
    try {
      if ("isLedger" in options && options.isLedger) {
        throw new CustomError21("Bitcoin Ledger signing not yet implemented");
      }
      return Promise.resolve({});
    } catch (error) {
      this.handleError("Failed to sign Bitcoin transaction", error);
    }
  }
  async signMessage(_message, _options) {
    try {
      return Promise.resolve("");
    } catch (error) {
      this.handleError("Failed to sign Bitcoin message", error);
    }
  }
  async signTypedData(_typedData, _options) {
    return Promise.resolve("");
  }
  async signAndSendTransaction(transaction, options) {
    try {
      this.validateAddress();
      const isLedgerSigner = "isLedger" in options && options.isLedger;
      const addressType = getAddressType(this.address);
      const txBroadcastUrl = this.getExplorerAddressUrl("/tx");
      const utxoSelection = await this.prepareSelection(transaction);
      let txHex;
      if (isLedgerSigner) {
        if (addressType === BITCOIN_ADDRESS_TYPES.P2TR) {
          throw new CustomError21("Ledger Taproot (P2TR) signing not yet implemented");
        }
        const derivationPath = options.derivationPath;
        const { btc } = await this.getLedgerSigner({ derivationPath });
        const txsUrl = this.getExplorerAddressUrl(`/address/${this.address}/txs`);
        const txs = await fetchTxHistory2(txsUrl);
        txHex = await createPaymentTx({
          btc,
          utxoSelection,
          derivationPath,
          address: this.address,
          network: this.network,
          txs,
          addressType
        });
      } else {
        const { keyPair } = this.initWalletFromOptions(options);
        if (addressType === BITCOIN_ADDRESS_TYPES.P2PKH) {
          throw new CustomError21("Bitcoin legacy p2pkh send transaction not yet implemented");
        }
        txHex = preparePsbtTx({
          utxoSelection,
          addressType,
          keyPair,
          network: this.network,
          fallbackAddress: this.address
        });
      }
      const { txid } = await broadcastTx(txBroadcastUrl, {
        method: "POST",
        body: txHex,
        headers: { "Content-Type": "text/plain" }
      });
      return makeTxResponse(txid, this.address, transaction.value);
    } catch (error) {
      this.handleError("Failed to sign and send Bitcoin transaction", error);
    }
  }
  async prepareSelection(transaction) {
    const MEDIUM_PRIORITY_FEE_RATE = 10;
    const MEDIUM_PRIORITY_CONFIRMATION_BLOCKS = 6;
    const [feeEstimates, utxos] = await Promise.all([
      fetchFeeEstimates(this.getExplorerAddressUrl("/fee-estimates")),
      fetchUTXOs(this.getExplorerAddressUrl(`/address/${this.address}/utxo`))
    ]);
    const mediumPriorityFeeRate = feeEstimates[MEDIUM_PRIORITY_CONFIRMATION_BLOCKS] || MEDIUM_PRIORITY_FEE_RATE;
    const confirmedUtxos = utxos.filter((utxo) => utxo.status.confirmed);
    if (confirmedUtxos.length === 0) {
      throw new CustomError21("No confirmed UTXOs available to create a transaction");
    }
    const inputsForSelection = confirmedUtxos.map((utxo) => ({
      txid: utxo.txid,
      vout: utxo.vout,
      value: utxo.value
    }));
    const targetValue = Number(transaction.value);
    const addressType = getAddressType(this.address);
    const { inputs, outputs, fee } = selectUtxosAndOutputs(
      inputsForSelection,
      transaction.to,
      targetValue,
      mediumPriorityFeeRate,
      addressType
    );
    return { inputs, outputs, fee, confirmedUtxos };
  }
  async getBalance() {
    const commonFields = {
      chainId: this.chainFullId,
      symbol: this.config.symbol
    };
    try {
      this.validateAddress();
      const url = this.getExplorerAddressUrl(`/address/${this.address}`);
      const data = await fetchBalance(url);
      const totalSatoshis = data.chain_stats.funded_txo_sum - data.chain_stats.spent_txo_sum + (data.mempool_stats.funded_txo_sum - data.mempool_stats.spent_txo_sum);
      const balanceInBTC = formatBitcoinAmount(totalSatoshis);
      return {
        total: balanceInBTC,
        available: balanceInBTC,
        ...commonFields
      };
    } catch (error) {
      this.handleError("Failed to get balance", error);
    }
  }
  async getTxHistory() {
    try {
      this.validateAddress();
      const url = this.getExplorerAddressUrl(`/address/${this.address}/txs`);
      const txs = await fetchTxHistory2(url);
      const normalizer = normalizeBitcoinTx(this.chainFullId, {
        explorerUrl: blockExplorerUrls[this.chainFullId],
        symbol: this.config.symbol
      });
      return txs.map((tx) => normalizer(tx, this.address));
    } catch (error) {
      this.handleError("Failed to get transaction history", error);
    }
  }
  getAddressIconUrl(_address) {
    return Promise.resolve("");
  }
  async getLedgerSigner({
    derivationPath
  }) {
    const transport = await TransportWebUSB3.create();
    const app = new Btc({ transport });
    const FORMAT_P2WPKH = "bech32";
    const getPublicKeyPair = async () => {
      const { publicKey, bitcoinAddress } = await app.getWalletPublicKey(derivationPath, {
        verify: false,
        format: FORMAT_P2WPKH
      });
      return { publicKey, address: bitcoinAddress };
    };
    const getAddress2 = async () => {
      const { address: address3 } = await getPublicKeyPair();
      return address3;
    };
    const close = async () => {
      await transport.close();
    };
    const signer = {
      getPublicKeyPair,
      getAddress: getAddress2,
      close,
      path: derivationPath,
      type: "ledger",
      btc: app
    };
    return signer;
  }
};

// src/providers/providers.ts
import { getEnv as getEnv3 } from "@umami/utils";
var Providers = {
  [ChainNamespaces.EVM]: EvmProvider,
  [ChainNamespaces.BITCOIN]: BitcoinProvider
};
var getDefaultChains = () => {
  const defaultChains = [ChainFullIds.ETHERLINK, ChainFullIds.ETHEREUM, ChainFullIds.BITCOIN];
  const testnets = [
    ChainFullIds.ETHERLINK_SHADOWNET,
    ChainFullIds.ETHEREUM_SEPOLIA,
    ChainFullIds.BITCOIN_TESTNET
  ];
  if (getEnv3("NODE_ENV") === "development") {
    return [...defaultChains, ...testnets];
  }
  return defaultChains;
};
var defaultDerivationPathTemplates = {
  [ChainIds.TEZOS]: defaultDerivationPathTemplate,
  [ChainIds.ETHERLINK]: evmDerivationPathTemplate,
  [ChainIds.ETHEREUM]: evmDerivationPathTemplate,
  [ChainIds.BSC]: evmDerivationPathTemplate,
  [ChainIds.BITCOIN]: derivationPathTemplate
};
var getDefaultDerivationPathByFullChainId = (fullChainId) => {
  switch (fullChainId) {
    default:
    case ChainFullIds.BITCOIN:
      return defaultDerivationPath["0"];
    case ChainFullIds.BITCOIN_TESTNET:
      return defaultDerivationPath["1"];
  }
};

// src/utils/wrapAllSettled.ts
var wrapAllSettled = async (promises) => {
  const results = await Promise.allSettled(promises);
  const errors = [];
  const fulfilled = results.flatMap((result) => {
    if (result.status === "rejected") {
      errors.push(result.reason);
      return [];
    }
    return result.value;
  });
  return { fulfilled, errors };
};

// src/ChainRegistry.ts
var ChainRegistry = class {
  chainProviders = {};
  constructor(configs, initParams) {
    this.init(configs, initParams);
  }
  get chainIds() {
    return Object.keys(this.chainProviders).filter(isChainFullId);
  }
  /**
   * Get chain IDs that have providers with configured addresses
   */
  get configuredChainIds() {
    return this.chainIds.filter((chainId) => {
      const provider = this.chainProviders[chainId];
      try {
        const address3 = provider?.getAddress();
        return !!address3;
      } catch {
        return false;
      }
    });
  }
  init(configs, initParams) {
    this.initDefaultProviders();
    if (initParams) {
      Object.entries(initParams).forEach(([chainId, params]) => {
        if (isDeprecated(chainId)) {
          return;
        }
        if (!isChainFullId(chainId)) {
          throw new CustomError22(`Invalid chainId: ${chainId}`);
        }
        const config = configs[chainId];
        this.addChainProvider(chainId, config, params);
      });
    }
  }
  /**
   * Initializes default blockchain providers for all chains specified in the {@link defaultChains} array.
   */
  initDefaultProviders() {
    getDefaultChains().forEach((chainId) => {
      const config = NETWORK_CONFIGS[chainId];
      this.addChainProvider(chainId, config);
    });
  }
  /**
   * @param chainId - The chain ID to get the provider for
   * @returns The provider for the given chain ID
   */
  getChainProvider(chainId) {
    const provider = this.chainProviders[chainId];
    if (!provider) {
      throw new CustomError22(`Provider for chain ${chainId} is not initialized or supported.`);
    }
    return provider;
  }
  addChainProvider(chainId, config, params) {
    const { namespace } = parseChainFullId(chainId);
    if (namespace === ChainNamespaces.TEZOS) {
      return;
    }
    if (!(namespace in Providers)) {
      throw new CustomError22(`Provider for chain ${chainId} is not supported.`);
    }
    if (params || !this.chainProviders[chainId]) {
      const Provider = Providers[namespace];
      this.chainProviders[chainId] = new Provider(config, params);
    }
  }
  removeChainProvider(chainId) {
    delete this.chainProviders[chainId];
  }
  /**
   * Creates wallets for all chains.
   */
  async createWallets(options) {
    if ("secretKey" in options) {
      const { multichain } = await normalizeSecretKey(options.secretKey);
      const walletPromises = Object.entries(multichain).flatMap(
        ([chainNamespace, secretKey]) => getDefaultChains().filter((chain) => {
          const { namespace } = parseChainFullId(chain);
          return namespace === chainNamespace;
        }).map((chainFullId) => this.createWallet(chainFullId, { secretKey }))
      );
      return Promise.all(walletPromises);
    }
    return Promise.all(this.chainIds.map((chainId) => this.createWallet(chainId, options)));
  }
  /**
   * Creates a wallet for a given chain.
   */
  async createWallet(chainId, options) {
    return this.getChainProvider(chainId).createWallet(options);
  }
  /**
   * Only signs the transaction without broadcasting it
   * @param transaction - The {@link Transaction} to sign
   * @param options - The {@link SignTransactionOptions} for the transaction signing
   * @returns The signed transaction
   */
  async signTransaction(chainId, transaction, options) {
    if ("secretKey" in options) {
      const { namespace } = parseChainFullId(chainId);
      if (!isMultichainChainNamespace(namespace)) {
        throw new CustomError22(`Unsupported chain namespace: ${namespace}`);
      }
      const { multichain } = await normalizeSecretKey(options.secretKey);
      const secretKey = multichain[namespace];
      return this.getChainProvider(chainId).signTransaction(transaction, {
        secretKey
      });
    }
    return this.getChainProvider(chainId).signTransaction(transaction, options);
  }
  /**
   * Signs a message
   * @param message - Message to sign
   * @param options - Signing options including credentials
   * @returns Signed message
   */
  async signMessage(chainId, message, options) {
    if ("secretKey" in options) {
      const { namespace } = parseChainFullId(chainId);
      if (!isMultichainChainNamespace(namespace)) {
        throw new CustomError22(`Unsupported chain namespace: ${namespace}`);
      }
      const { multichain } = await normalizeSecretKey(options.secretKey);
      const secretKey = multichain[namespace];
      return this.getChainProvider(chainId).signMessage(message, {
        secretKey
      });
    }
    return this.getChainProvider(chainId).signMessage(message, options);
  }
  /**
   * Signs typed data according to EIP-712
   * @param typedData - EIP-712 typed data to sign
   * @param options - Signing options including credentials
   * @returns Signed typed data
   */
  async signTypedData(chainId, typedData, options) {
    if ("secretKey" in options) {
      const { namespace } = parseChainFullId(chainId);
      if (!isMultichainChainNamespace(namespace)) {
        throw new CustomError22(`Unsupported chain namespace: ${namespace}`);
      }
      const { multichain } = await normalizeSecretKey(options.secretKey);
      const secretKey = multichain[namespace];
      return this.getChainProvider(chainId).signTypedData(typedData, {
        secretKey
      });
    }
    return this.getChainProvider(chainId).signTypedData(typedData, options);
  }
  /**
   * Signs and sends a transaction to blockchain
   * @param transaction - The {@link Transaction} to sign and send
   * @param options - The {@link SignTransactionOptions} for the transaction signing
   * @returns The {@link TransactionResponse} response
   */
  async signAndSendTransaction(chainId, transaction, options) {
    if ("secretKey" in options) {
      const { namespace } = parseChainFullId(chainId);
      if (!isMultichainChainNamespace(namespace)) {
        throw new CustomError22(`Unsupported chain namespace: ${namespace}`);
      }
      const { multichain } = await normalizeSecretKey(options.secretKey);
      const secretKey = multichain[namespace];
      return this.getChainProvider(chainId).signAndSendTransaction(transaction, {
        secretKey
      });
    }
    return await this.getChainProvider(chainId).signAndSendTransaction(transaction, options);
  }
  async addChain(chainId, config, options) {
    this.addChainProvider(chainId, config);
    if ("secretKey" in options) {
      const { namespace } = parseChainFullId(chainId);
      if (!isMultichainChainNamespace(namespace)) {
        throw new CustomError22(`Unsupported chain namespace: ${namespace}`);
      }
      const { multichain } = await normalizeSecretKey(options.secretKey);
      const secretKey = multichain[namespace];
      return await this.createWallet(chainId, { secretKey });
    }
    return await this.createWallet(chainId, options);
  }
  removeChain(chainId) {
    const provider = this.chainProviders[chainId];
    if (provider?.disconnect) {
      provider.disconnect();
    }
    this.removeChainProvider(chainId);
  }
  async getLedgerSigner(chainId, derivationPath) {
    return await this.getChainProvider(chainId).getLedgerSigner({ derivationPath });
  }
  getExplorerUrl(chainId) {
    return this.getChainProvider(chainId).getExplorerUrl();
  }
  async estimate(chainId, transaction) {
    return this.getChainProvider(chainId).estimate(transaction);
  }
  async getAllBalances() {
    const promises = this.configuredChainIds.map(
      (chainId) => this.getChainProvider(chainId).getBalance()
    );
    return wrapAllSettled(promises.map((p) => p.then((balance) => [balance])));
  }
  async getTotalBalance() {
    const balances = await this.getAllBalances();
    return balances.fulfilled.reduce(
      (acc, balance) => ({
        ...acc,
        total: (Number(acc.total) + Number(balance.total)).toString(),
        available: (Number(acc.available) + Number(balance.available)).toString()
      }),
      { total: "0", available: "0" }
    );
  }
  async getAllTxHistory() {
    const promises = this.configuredChainIds.map(
      (chainId) => this.getChainProvider(chainId).getTxHistory()
    );
    return wrapAllSettled(promises);
  }
  async getAllTokensTransfers() {
    const promises = this.configuredChainIds.map((chainId) => {
      const provider = this.getChainProvider(chainId);
      return provider.getTokensTransfers?.() ?? Promise.resolve([]);
    });
    return wrapAllSettled(promises);
  }
  async getCombinedTransactions() {
    const [txHistory, tokensTransfers] = await Promise.all([
      this.getAllTxHistory(),
      this.getAllTokensTransfers()
    ]);
    return {
      fulfilled: [...txHistory.fulfilled, ...tokensTransfers.fulfilled],
      errors: [...txHistory.errors, ...tokensTransfers.errors]
    };
  }
  async getAllTokens() {
    const promises = this.configuredChainIds.map((chainId) => {
      const provider = this.getChainProvider(chainId);
      return provider.getTokens?.() ?? Promise.resolve([]);
    });
    return wrapAllSettled(promises);
  }
  async getAllNfts() {
    const promises = this.configuredChainIds.map((chainId) => {
      const provider = this.getChainProvider(chainId);
      return provider.getNfts?.() ?? Promise.resolve([]);
    });
    return wrapAllSettled(promises);
  }
};

// src/hooks/useChainRegistry.ts
var ChainRegistryService = class {
  static instance = null;
  static initialize(configs, initParams) {
    this.instance = new ChainRegistry(configs, initParams);
  }
  static getInstance() {
    if (!this.instance) {
      throw new Error("ChainRegistry not initialized");
    }
    return this.instance;
  }
  static reset() {
    this.instance = null;
  }
};
var useChainRegistry = () => {
  const registry = ChainRegistryService.getInstance();
  const reset = () => ChainRegistryService.reset();
  const initialize = (configs, initParams) => ChainRegistryService.initialize(configs, initParams);
  return {
    reset,
    initialize,
    registry
  };
};
if (process.env.NODE_ENV === "development") {
  window.chainRegistry = ChainRegistryService;
}

// src/hooks/useEstimate.ts
var useEstimate = () => {
  const { registry } = useChainRegistry();
  return async (chainId, operations, network) => {
    const { namespace } = parseChainFullId(chainId);
    let estimatedOperations;
    if (namespace === ChainNamespaces.TEZOS) {
      estimatedOperations = await estimate(operations, network);
    } else {
      const preparedOperation = prepareChainTransaction(operations.operations[0]);
      const estimates = await registry.estimate(chainId, preparedOperation);
      estimatedOperations = {
        estimates: [estimates],
        ...operations
      };
    }
    return estimatedOperations;
  };
};

// src/hooks/useSignAndSend.ts
var useSignAndSend = () => {
  const { registry } = useChainRegistry();
  return async ({
    chainId,
    operations,
    options,
    tezosToolkit,
    estimates
  }) => {
    let operation;
    const { namespace } = parseChainFullId(chainId);
    if (namespace === ChainNamespaces.TEZOS) {
      operation = await executeOperations({ ...operations, estimates }, tezosToolkit);
    } else {
      const preparedOperation = prepareChainTransaction(operations.operations[0]);
      operation = await registry.signAndSendTransaction(chainId, preparedOperation, options);
    }
    return operation;
  };
};
export {
  AVAILABLE_DERIVATION_PATH_TEMPLATES,
  AddressSchema,
  BITCOIN_ADDRESS_TYPES,
  BLOCK_TIME,
  BTC_DECIMALS,
  BaseEvmProvider,
  BaseProvider,
  BitcoinProvider,
  ChainFullIds,
  ChainIds,
  ChainNamespaces,
  ChainRegistry,
  ChainRegistryService,
  CryptoSymbols,
  DEFAULT_ACCOUNT_LABEL,
  DEFAULT_DERIVATION_PATH_TEMPLATE,
  DEFAULT_ETHERS_DECIMALS,
  DEPRECATED_CHAINS,
  DefaultNetworks,
  ETHEREUM_NETWORK_CONFIG,
  ETHERLINK_NETWORK_CONFIG,
  EvmProvider,
  FA12TokenSchema,
  FA12_TRANSFER_ARG_TYPES,
  FA2TokenSchema,
  FA2_TRANSFER_ARG_TYPES,
  FakeSigner,
  Hints,
  IS_STAGING,
  LedgerSigner2 as LedgerSigner,
  MAINNET,
  MICHELSON,
  MICHELSON_SHADOWNET,
  NETWORK_CONFIGS,
  NFTSchema,
  OperationTypes,
  POLLING_INTERVAL,
  POLLING_STALE_TIME,
  PREVIEWNET,
  PRICING_API_URL,
  Providers,
  SATS_PER_BTC,
  SHADOWNET,
  SafeSendAssetType,
  SecretKeyTypes,
  TEZ,
  TEZ_DECIMALS,
  Titles,
  TransactionDirections,
  artifactUri,
  bitcoinConfig,
  bitcoinToSatoshi,
  btcDerivationPathTemplates,
  contractCallFixture,
  convertEvmPrivateKeyToTezosSpsk,
  convertTezosSpskToEvmPrivateKey,
  curveToDerivationType,
  decodeBeaconPayload,
  decryptSecretKey,
  defaultDerivationPath,
  defaultDerivationPathTemplate,
  defaultDerivationPathTemplates,
  delegationFixture,
  derivationPathTemplate,
  derivePublicKeyPair,
  deriveSecretKey,
  detectChainNamespace,
  detectSecretKeyType,
  estimate,
  ethereumConfig,
  etherlinkConfig,
  evmDerivationPathTemplate,
  executeOperations,
  fetchNfts,
  fetchPrices,
  fetchTokens,
  fetchTokensTransfers,
  fetchTxHistory,
  finalizeUnstakeFixture,
  formatBalance,
  formatBitcoinAmount,
  formatCurrency,
  formatEthersAmount,
  formatPkh,
  formatTezAmount,
  formatTezAmountMin0Decimals,
  formatToNormalUnit,
  formatTokenAmount,
  formatTokenBalance,
  formatUsdAmount,
  fromRawToken,
  fromRawTokenBalance,
  fullId,
  generateChainFullIdsFromConfigs,
  generateHash,
  generateKeys,
  getAccountGroupLabel,
  getAccounts,
  getAddressType,
  getAddressTypeFromPath,
  getAllClaimTxForReceiver,
  getAllClaims,
  getBakers,
  getChainAddress,
  getChainConfig,
  getChainData,
  getChainDecimals,
  getChainFullId,
  getChainFullIdFromNetwork,
  getChainPriority,
  getCombinedOperations,
  getDefaultChains,
  getDefaultDerivationPath,
  getDefaultDerivationPathByFullChainId,
  getDelegations,
  getDerivationPathNextIndex,
  getEnv,
  getHash,
  getIPFSurl,
  getIconUrl,
  getLatestBlock,
  getLedgerPublicKeyPair,
  getNativeTokenPriority,
  getNftId,
  getOperationsByHash,
  getOriginations,
  getPayments,
  getPendingUnstakeRequests,
  getProtocolSettings,
  getPublicKeyPairFromSk,
  getRawAmount,
  getRealAmount,
  getRecvForSafeSendTx,
  getRelatedTokenTransfers,
  getSafeSendContracts,
  getSafeSendOpsForAddress,
  getSafeSendTransactions,
  getSigningTypeFromPayload,
  getSmallestUnit,
  getStakingOperations,
  getTezosPriceInUSD,
  getTokenBalances,
  getTokenContract,
  getTokenContractName,
  getTokenId,
  getTokenName,
  getTokenPrice,
  getTokenSymbol,
  getTokenTransfers,
  getTransactionDirection,
  getTransactions,
  getTxAmount,
  getTxDirection,
  getTxToFrom,
  headlessLambda,
  isAccountRevealed,
  isAddressValid,
  isBitcoinAddressValid,
  isChainAddressValid,
  isChainFullId,
  isChainId,
  isChainNamespace,
  isDefault,
  isDeprecated,
  isEvmAddressValid,
  isMultichainChainNamespace,
  isMultichainToken,
  isSaplingAddress,
  isTezosNft,
  isTezosToken,
  isValidContractPkh,
  isValidEvmPrivateKey,
  isValidImplicitPkh,
  isValidMichelson,
  isValidSmartRollupPkh,
  makeAccountOperations,
  makeDerivationPath,
  makeFA12TransactionParameter,
  makeFA2TransactionParameter,
  makeMultisigApproveOrExecuteOperation,
  makeMultisigProposeOperation,
  makeSigner,
  makeToolkit,
  metadataUri,
  mimeType,
  mockAccountLabel,
  mockBaker,
  mockBitcoinCompatibleImplicitAddress,
  mockContractAddress,
  mockContractCall,
  mockContractContact,
  mockContractOrigination,
  mockDelegation,
  mockDelegationOperation,
  mockEvmCompatibleImplicitAddress,
  mockFA12Operation,
  mockFA12Token,
  mockFA1TokenRaw,
  mockFA2Operation,
  mockFA2Token,
  mockFA2TokenRaw,
  mockFinalizeUnstakeOperation,
  mockImplicitAccount,
  mockImplicitAddress,
  mockImplicitAddressByChainId,
  mockImplicitContact,
  mockLedgerAccount,
  mockMnemonicAccount,
  mockMultisigAccount,
  mockNFTBalance,
  mockNFTRaw,
  mockNFTToken,
  mockNftOperation,
  mockPk,
  mockSecretKeyAccount,
  mockSmartRollupAddress,
  mockSocialAccount,
  mockStakeOperation,
  mockTezOperation,
  mockTokenTransaction,
  mockTzktTezTransfer,
  mockUndelegationOperation,
  mockUnstakeOperation,
  mutezToTez,
  networks2 as networks,
  normalizeBitcoinTx,
  normalizeEvmKey,
  normalizeEvmNFT,
  normalizeEvmToken,
  normalizeEvmTokenTransfer,
  normalizeEvmTx,
  normalizeSecretKey,
  normalizeTezosToMultichainOp,
  normalizeTimestamp,
  operationSign,
  operationToTaquitoOperation,
  operationsToBatchParams,
  operationsToWalletParams,
  originationFixture,
  parseBitcoinAddress,
  parseChainAddress,
  parseChainAddressByNameSpace,
  parseChainFullId,
  parseContractPkh,
  parseEvmAddress,
  parseImplicitPkh,
  parseNftContractAddress,
  parsePkh,
  parseSaplingPkh,
  parseSmartRollupPkh,
  parseToNormalUnit,
  parseToSmallestUnit,
  parseTokenContractAddress,
  partialOperationToOperation,
  prepareChainTransaction,
  prettyTezAmount,
  rawAccountFixture,
  royalties,
  sanitizeTokenName,
  satoshiToBitcoin,
  selectRandomElements,
  selectUtxosAndOutputs,
  setSafeSendContracts,
  signAllTaprootInputs,
  sortedByLastUpdate,
  stakeFixture,
  tezToMutez,
  thumbnailUri,
  toAccountOperations,
  toBatchLambda,
  toLambda,
  toXOnly,
  tokenContractName,
  tokenDecimals,
  tokenName,
  tokenNameSafe,
  tokenPrettyAmount,
  tokenSymbol,
  tokenSymbolSafe,
  tokenTransferFixture,
  tokenUri,
  totalFee,
  transactionFixture,
  transactionTypes,
  truncate,
  unstakeFixture,
  useChainRegistry,
  useEstimate,
  useSignAndSend,
  verifiedTokens,
  withRateLimit,
  withTimeout
};
